// Cape Town, "Table Mountain from Bo-Kaap" (a warm December night, the south-easter blowing). From the top of a steep
// cobbled lane in Bo-Kaap, on the slope of Signal Hill, we look south over the city bowl to the whole amphitheatre:
// Devil's Peak on the left, the long flat top of Table Mountain in the middle with its floodlit north face, and Lion's
// Head's bald dome on the right. The "Cape Doctor" builds the tablecloth on the flat top and clears it again: a thin
// veil of cloud lying on the table that spills over the front edge and evaporates as it sinks. The cableway's cabins
// cross the lit face, the Southern Cross hangs low in the notch above Devil's Peak saddle, hikers' headlamps come down
// Lion's Head. The lane is lined with Bo-Kaap's flat-roofed cottages, stepping down the hill in pink, lime, blue,
// yellow and violet, with stoeps, sash windows and a mosque's minaret beyond; neighbours chat on a stoep, a minibus
// taxi crosses at the bottom, washing flies on a roof, hadedas roost on a parapet, and pink protea petals are blown
// almost sideways across the scene at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, hash, rng, bayer, hex,
  mix, makeCanvas, rect, makeHalo, drawHalo, text, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
// The mountains are drawn in screen space (compressed artistically, not rearranged: Devil's Peak east, the table in
// the middle, Lion's Head west). The foreground is projected: eye at the origin 1.7 m above the top of the lane, the lane
// dropping away at 16 %, x = VPX + F·lat / z, y = HZ − F·alt / z (alt relative to the eye; we stand on the top step).
const HZ = 166;                                       // eye level: the level parapets converge here
const VPX = 238, F = 260, EYE = 2.0, SLOPE = 0.16;
const proj = (lat, z, alt) => [VPX + F * lat / z, HZ - F * alt / z];
const ground = z => -EYE - SLOPE * Math.min(z, CROSS.z0);   // the lane's surface, flat along the cross street
const LFAC = -7, RFAC = 8, KERB = 3.5;            // left and right facades, the kerbs (lateral metres)
const CROSS = { z0: 50, z1: 58 };                     // the cross street at the bottom of the lane
const FAR_Z = 58.5;                                   // the houses facing us across the cross street
const TM = { x0: 142, x1: 328, top: 57 };             // Table Mountain's flat top: east corner .. west corner
const DP = { x: 86, y: 76 };                          // Devil's Peak's tip (≈ 11.9° up, the table's edge ≈ 13.4–14.7°)
const LH = { x: 428, y: 76 };                         // Lion's Head's tip (≈ 11.7°: lower, but much nearer)
const LH_R = 10.5, LH_DOME = 7, LH_WALL = 6, LH_WW = 1.6;   // its bald dome: half-width, height; the walls under it: drop, width
const CAB = { x0: 312, y0: 58, x1: 318, y1: 121 };    // the cableway: upper station on the edge, lower on Tafelberg Road
const BB = { x: 330, y: 114, w: 46, h: 20 };          // the painted ad on a gable wall right of the table (the site's billboard)
const BBW = { x0: 324, x1: 377, top: 106 };           // that gable wall (its foot hides behind the right-hand row)
const BB_LAMPS = [9, 36];                             // the ad's two gooseneck lamps (x offsets on the face)
const CRUX = { x: 116, y: 84 };                       // the Southern Cross, low in the SSE, lying on its side

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** '#rrggbb' between two colours (for the pixel buffers; core's mix() gives an rgb() string for fillStyle) */
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b);
  return '#' + A.map((v, i) => Math.round(lerp(v, B[i], t)).toString(16).padStart(2, '0')).join('');
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** a sprite from rows of palette keys ('.' = transparent) */
function sprite(rows, pal) {
  const p = pix(rows[0].length, rows.length);
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') p.set(i, j, pal[row[i]]); });
  return p.done();
}
/** FONT3 text into a pix buffer (sc = pixel scale) */
function textP(p, s, x, y, col, sc = 1) {
  let cx = x;
  for (const ch of s) { const g = FONT3[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.fill(cx + i * sc, y + j * sc, sc, sc, col); }); cx += 4 * sc; }
}
/** flattened dithered ellipse of light (pools on the cobbles), drawn additively */
function makePool(rx, ry, col, maxA) {
  const w = rx * 2 + 1, h = ry * 2 + 1, p = pix(w, h), c = hex(col);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const d = Math.hypot((x - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.5) * 6 + bayer(x, y)) / 6;
    if (q > 0) p.set(x, y, c, Math.round(q * maxA * 255));
  }
  return { c: p.done(), rx, ry };
}
function drawPool(pl, x, y, a) {
  if (a <= 0.004) return;
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = Math.min(1, a);
  ctx.drawImage(pl.c, Math.round(x) - pl.rx, Math.round(y) - pl.ry);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
/** a flicker level: 1, with stutters inside the event windows */
function flick(t, ev, salt) {
  if (!inEvent(t, ev)) return 1;
  const hv = hash(salt, Math.floor(t * 20), 3);
  return hv > 0.45 ? 1 : hv > 0.2 ? 0.45 : 0.1;
}
/** fill a convex screen-space polygon (build time): colFn(x, y) returns a colour or null */
function poly(p, pts, colFn) {
  let y0 = Infinity, y1 = -Infinity;
  for (const [, y] of pts) { y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
  for (let y = Math.max(0, Math.ceil(y0 - 0.5)); y <= Math.min(H - 1, Math.floor(y1 - 0.5)); y++) {
    const yc = y + 0.5;
    let xa = Infinity, xb = -Infinity;
    for (let k = 0; k < pts.length; k++) {
      const [ax, ay] = pts[k], [bx, by] = pts[(k + 1) % pts.length];
      if ((ay <= yc && by > yc) || (by <= yc && ay > yc)) {
        const x = ax + (yc - ay) / (by - ay) * (bx - ax);
        xa = Math.min(xa, x); xb = Math.max(xb, x);
      }
    }
    if (xa > xb) continue;
    for (let x = Math.max(0, Math.round(xa)); x < Math.min(W, Math.round(xb)); x++) {
      const col = colFn(x, y);
      if (col) p.set(x, y, col);
    }
  }
}
/** a 3D quad (four [lat, z, alt] corners) projected and filled */
const quad3 = (p, corners, colFn) => poly(p, corners.map(([l, z, a]) => proj(l, z, a)), colFn);
/** a gust of the south-easter: 0 calm .. 1 at the height of a gust (loop seconds) */
const GUSTS = [[14, 27], [52, 63], [96, 112], [141, 150], [178, 194], [214, 226]];
const gust = t => { let g = 0; for (const [a, b] of GUSTS) g = Math.max(g, env(t, a, b, GUST_F)); return g; };
// how far a gust has pushed things downwind: the exact integral of the envelopes (smoothstep ramps of GUST_F s), with the
// drift over the whole loop taken out so it stays periodic
const GUST_F = 3, ramp = u => u * u * u - u * u * u * u / 2;                 // ∫0^u of smoothstep
const GUST_SUM = GUSTS.reduce((s, [a, b]) => s + (b - a - GUST_F), 0);
function gustPush(t) {
  let s = 0;
  for (const [a, b] of GUSTS) {
    if (t <= a) continue;
    if (t >= b) { s += b - a - GUST_F; continue; }
    if (t < a + GUST_F) s += GUST_F * ramp((t - a) / GUST_F);
    else if (t < b - GUST_F) s += GUST_F / 2 + t - a - GUST_F;
    else { const w = (t - b + GUST_F) / GUST_F; s += GUST_F / 2 + (b - a - 2 * GUST_F) + GUST_F * (w - ramp(w)); }
  }
  return s - GUST_SUM * t / LOOP;
}

/* ================= The skyline: Devil's Peak, the table, Lion's Head ================= */
/** Devil's Peak: a sharp peak with concave flanks, the long left one towards Mowbray, the right one to the saddle */
function dpY(x) {
  const d = x - DP.x, a = Math.abs(d);
  if (d < 0) return DP.y + 13 * (1 - Math.exp(-a / 7)) + a * 0.3 + 1.5 * Math.sin(x * 0.21) * smooth(14, 30, a) - 3 * Math.exp(-(((a - 44) / 9) ** 2));
  return DP.y + 15 * (1 - Math.exp(-a / 6.5)) + a * 0.32 + 0.8 * Math.sin(x * 0.5) * smooth(8, 20, a);
}
/** Table Mountain: the level front table (a hair lower towards the cable station), sheer ends, the slopes beyond */
function tmY(x) {
  if (x >= TM.x0 && x <= TM.x1) {
    const notch = Math.max(0, 1.6 - Math.abs(x - 236) * 0.5);                 // the top of Platteklip Gorge
    return TM.top + 1.4 - (x - TM.x0) / (TM.x1 - TM.x0) * 2 + notch + (hash(x >> 2, 3, 71) < 0.18 ? 0.6 : 0);
  }
  if (x < TM.x0) {                                                            // the eastern buttress, then down to the saddle
    const d = TM.x0 - x;
    return d <= 6 ? TM.top + d * d * 0.55 : TM.top + 19.8 + (d - 6) * 2.1 + Math.sin(d * 0.9) * 0.6;
  }
  const d = x - TM.x1;                                                        // the western end, then Kloof Corner down to Kloof Nek
  return d <= 5 ? TM.top + 1.4 + d * d * 0.5 : TM.top + 13.9 + (d - 5) * 0.88 + 2.2 * smooth(18, 24, d) - 2.2 * smooth(30, 36, d) + Math.sin(d * 0.7) * 0.5;
}
/** Lion's Head: a bald, rounded dome of rock with sheer sides on a steep neck, the long shoulder down to Signal Hill (off
 *  to the right). The dome is a superellipse, broad and blunt where Devil's Peak is a point */
function lhY(x) {
  const d = x - LH.x, a = Math.abs(d), foot = LH.y + LH_DOME + LH_WALL, n = a - LH_R - LH_WW;
  if (a <= LH_R) return LH.y + LH_DOME * (1 - Math.pow(1 - Math.pow(a / LH_R, 1.6), 0.625));   // the dome
  if (n <= 0) return LH.y + LH_DOME + (a - LH_R) / LH_WW * LH_WALL + (d > 0 ? 0.6 : 0);       // its sheer rock walls
  if (d < 0) return foot + n * 1.7 + 1.4 * (1 - Math.exp(-n / 2));                             // down the neck to Kloof Nek
  return foot + 0.6 + n * 0.5 + 1.2 * (1 - Math.exp(-n / 2)) - 2 * Math.exp(-(((a - 34) / 6) ** 2));   // the long shoulder towards Signal Hill
}
const ridge = x => Math.min(dpY(x), tmY(x), lhY(x));

/* ================= Sky: a deep summer night, warmed by the city along the mountains ================= */
const SKY = ['#04071a', '#070a20', '#0a0e28', '#0e1130', '#121538', '#171840', '#1d1b48', '#241f4e', '#2c2354', '#352758',
  '#402b5a', '#4c305c', '#58355c', '#663b5c'];
// the Milky Way rising steeply from the Cross, up and to the left (Carina, Vela, Puppis), washed out by the city low down
function milky(x, y) {
  const d = (x - CRUX.x) * 0.9 - (y - CRUX.y) * 0.44, core = Math.exp(-(d * d) / (2 * 13 * 13));
  const clumps = 0.5 + 0.9 * vnoise(x / 12, y / 12, 45) * vnoise(x / 5, y / 5, 46);
  const dust = Math.exp(-((d + 3) ** 2) / (2 * 3 * 3)) * (0.5 + 0.5 * vnoise(x / 8, y / 8, 44));
  return Math.max(0, core * clumps - dust * 0.5) * smooth(96, 30, y) * smooth(-40, 20, y + x * 0.2);
}
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 150), 1.25) * 0.8 + milky(i, y) * 0.3;
    v += Math.max(0, 1 - Math.hypot((i - 236) / 260, (y - 150) / 90)) * 0.18;   // the city bowl's glow behind the mountains
    v += Math.max(0, 1 - Math.hypot((i - 236) / 120, (y - 50) / 16)) * 0.1;     // the floodlights lifting the haze over the table
    p.set(i, y, pick(SKY, v + (vnoise(i / 30, y / 12, 5) - 0.5) * 0.04, i, y));
  }
  return p.done();
}
let STARS = [];
function makeStars() {
  const r = rng(31), out = [];
  for (let k = 0; k < 1500 && out.length < 130; k++) {
    const x = Math.floor(r() * W), y = 1 + Math.floor(r() * 120);
    if (y > ridge(x) - 5 - smooth(0, 60, y) * 6) continue;                     // not in the haze low over the ridge
    if (x < 122 && y < 48) continue;                                            // clear of the title overlay
    if (Math.abs(x - 236) < 104 && y > 40) continue;                            // the tablecloth's glow washes them out
    if (Math.hypot(x - CRUX.x, y - CRUX.y) < 14) continue;                      // leave the Cross its own patch of sky
    if (r() > 0.9 - y / 170 + milky(x, y) * 1.5) continue;
    const bright = r() < 0.08;
    out.push({ x, y, k: 20 + Math.floor(r() * 60), ph: r(), b: bright ? 0.85 : 0.2 + r() * 0.45, bright, col: r() < 0.25 ? '#ffe4c4' : r() < 0.5 ? '#cfe0ff' : '#f0eeff' });
  }
  return out;
}
// the Southern Cross rising in the SSE: its long axis points up and right, to the south celestial pole; Gacrux (orange)
// is the lower-left end, Acrux the upper right, Mimosa and Delta the crossbar, little Epsilon off-centre
const CRUX_STARS = [
  { dx: 7, dy: -7, b: 1, s: 1, col: '#e8f0ff', k: 37 },      // Acrux
  { dx: -7, dy: 6, b: 0.85, s: 1, col: '#ffc890', k: 29 },   // Gacrux
  { dx: 5, dy: 5, b: 0.9, s: 1, col: '#dfe8ff', k: 41 },     // Mimosa
  { dx: -4, dy: -4, b: 0.92, s: 0, col: '#e8eeff', k: 33 },  // Delta
  { dx: 2, dy: -3, b: 0.34, s: 0, col: '#ffe8d0', k: 23 },   // Epsilon
];
function drawStars(t) {
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    ctx.fillStyle = s.col; ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.bright) { ctx.globalAlpha = a * 0.3; ctx.fillRect(s.x - 1, s.y, 1, 1); ctx.fillRect(s.x + 1, s.y, 1, 1); ctx.fillRect(s.x, s.y - 1, 1, 1); ctx.fillRect(s.x, s.y + 1, 1, 1); }
  }
  for (const s of CRUX_STARS) {
    const a = s.b * (0.8 + 0.2 * wave(t, s.k, s.dx * 0.1)), x = CRUX.x + s.dx, y = CRUX.y + s.dy;
    ctx.fillStyle = s.col; ctx.globalAlpha = a; ctx.fillRect(x, y, 1, 1);
    if (s.s) { ctx.globalAlpha = a * 0.4; ctx.fillRect(x - 1, y, 1, 1); ctx.fillRect(x + 1, y, 1, 1); ctx.fillRect(x, y - 1, 1, 1); ctx.fillRect(x, y + 1, 1, 1); }
  }
  ctx.globalAlpha = 1;
  halo(HL.crux, CRUX.x + 7, CRUX.y - 7, 0.5); halo(HL.crux, CRUX.x + 5, CRUX.y + 5, 0.4); halo(HL.cruxG, CRUX.x - 7, CRUX.y + 6, 0.4);
}
/* thin streaks of cloud high up, pushed across by the south-easter aloft */
function renderClouds() {
  const h = 30, p = pix(W, h), r = rng(57), puffs = [];
  for (let k = 0; k < 6; k++) puffs.push({ x: r() * W, y: 6 + r() * 18, rx: 26 + r() * 46, ry: 1.3 + r() * 1.5 });
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = -9;
    for (const q of puffs) { let dx = x - q.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx / q.rx) ** 2 - ((y - q.y) / q.ry) ** 2); }
    v += 0.25 * Math.sin(TAU * 7 * x / W + y * 0.9) * Math.sin(TAU * 3 * x / W);
    if (v <= bayer(x, y) * 0.6) continue;
    p.set(x, y, v > 0.6 ? '#2e2c56' : '#24234a');
  }
  return p.done();
}
/** a satellite gliding over, catching the sun long after sunset (a steady dot, fading as it enters the Earth's shadow) */
const SAT = { t0: 158, t1: 222, x0: 150, y0: 8, x1: 452, y1: 44 };
function drawSatellite(t) {
  if (t < SAT.t0 || t >= SAT.t1) return;
  const s = (t - SAT.t0) / (SAT.t1 - SAT.t0);
  ctx.globalAlpha = 0.75 * smooth(0, 0.08, s) * (1 - smooth(0.75, 1, s));
  rect(ctx, Math.round(lerp(SAT.x0, SAT.x1, s)), Math.round(lerp(SAT.y0, SAT.y1, s)), 1, 1, '#e8ecff');
  ctx.globalAlpha = 1;
}
/** a jet high over the city, nav lights and a strobe */
const PLANE = { t0: 62, t1: 150, x0: 470, x1: 150, y0: 20, y1: 34 };
function drawPlane(t) {
  if (t < PLANE.t0 || t >= PLANE.t1) return;
  const s = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(PLANE.x0, PLANE.x1, s)), y = Math.round(lerp(PLANE.y0, PLANE.y1, s));
  rect(ctx, x - 2, y, 5, 1, '#262450'); rect(ctx, x, y - 1, 1, 1, '#262450');
  rect(ctx, x - 2, y, 1, 1, '#ff4a4a'); rect(ctx, x + 2, y + 1, 1, 1, '#4aff8a');
  const f = frac(t);
  if (f < 0.07 || (f > 0.17 && f < 0.22)) { rect(ctx, x, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x, y - 1, 0.9); }
}

/* ================= The mountains (one static canvas) ================= */
// the floodlit sandstone of the table's north face, the fynbos and forest of the lower slopes, the unlit peaks either side
const ROCK = ['#15131f', '#1d1a28', '#272232', '#322b3a', '#3e3542', '#4c404a', '#5c4c52', '#6e5a5a', '#826a62', '#987c6c',
  '#ae907a', '#c4a68a', '#d8bc9e', '#ead2b4'];
const FOREST = ['#080c16', '#0b111c', '#0f1622', '#141c28', '#1a232e', '#212a34', '#29323a'];
const PEAK = ['#0a0c1e', '#0e1024', '#12142b', '#171a33', '#1d203b', '#252844', '#2e3250'];
/** the upper edge of the suburbs climbing the lower slopes (where the city lights begin) */
const cityTop = x => Math.round(128 + 3 * Math.sin(x * 0.045 + 0.6) + 1.6 * Math.sin(x * 0.13) + 7 * smooth(330, 396, x) - 4 * smooth(396, 440, x)
  + 4 * (1 - smooth(40, 120, x)));
/** where the table's cliffs end and the talus begins */
const cliffBot = x => 93 + 3 * Math.sin(x * 0.07 + 1) + 6 * (vnoise(x / 5, 0, 17) - 0.5) + 6 * (1 - smooth(142, 162, x)) - 8 * (1 - smooth(128, 142, x)) + 4 * smooth(314, 330, x);
const flood = x => smooth(126, 178, x) * (1 - smooth(296, 346, x));          // the floodlights' reach along the face
const LEDGES = [[0.3, 1], [0.55, 0.7], [0.8, 1]];                             // bedding ledges (fraction of the cliff, strength)
function gorge(x, y) {                                                        // Platteklip Gorge: a V cleft down the middle
  const dy = y - TM.top, cx = 236 + dy * 0.06, w = 3.6 - dy * 0.085;
  return w > 0 && dy >= 0 ? Math.abs(x + 0.5 - cx) / w : 9;
}
function renderMountains() {
  const p = pix(W, H);
  for (let x = 0; x < W; x++) {
    const ct = cityTop(x) + 2;
    // Devil's Peak (the farthest): unlit, a cool rim on its ridge, ravines down its face
    const yd = Math.round(dpY(x));
    for (let y = Math.max(0, yd); y < ct; y++) {
      const dd = y - yd;
      let v = 0.32 + (vnoise(x / 3, y / 9, 21) - 0.5) * 0.3 - smooth(0, 40, dd) * 0.12 + 0.18 * smooth(96, 136, x) * smooth(4, 26, dd);   // spill from the lights
      if (vnoise(x / 1.6, y / 12, 22) > 0.78) v -= 0.14;
      if (dd === 0) v = 0.86; else if (dd === 1) v += 0.16;
      p.set(x, y, pick(PEAK, v, x, y));
    }
    // Table Mountain: the cliffs floodlit across the middle, the ends and the talus in shadow
    const yt = Math.round(tmY(x)), cb = cliffBot(x), fl = flood(x);
    for (let y = Math.max(0, yt); y < ct; y++) {
      const dy = y - yt;
      let col;
      if (y < cb) {
        const u = clamp((y - yt) / Math.max(4, cb - yt), 0, 1);
        let L = 0.12 + fl * (0.4 + 0.12 * Math.sin(Math.PI * Math.min(1, u * 1.15))) - (1 - fl) * 0.02;
        L += (vnoise(x / 3.2, y / 40, 11) - 0.5) * 0.34 * (0.45 + fl);        // broad buttresses lit, the bays between in shade
        if (vnoise(x / 1.2, y / 16, 12) < 0.16) L -= 0.13;                     // vertical joints
        for (const [uk, a] of LEDGES) {                                        // the bedding: ledges running across the face
          const e = (u - uk - 0.025 * Math.sin(x * 0.07 + uk * 9)) * (cb - yt);
          if (e >= 0 && e < 1) L -= 0.2 * a; else if (e >= -1 && e < 0) L += 0.07 * a;
        }
        L += (hash(x, y, 19) - 0.5) * 0.05;
        if (dy === 0) L += 0.12; else if (dy === 1) L -= 0.08;                 // the lit lip, a shadow under it
        const g = gorge(x, y);
        if (g < 1) L = g > 0.6 ? L * 0.85 : 0.08 + 0.12 * fl * g;              // the gorge in shadow
        else if (g < 1.4 && x < 236 + dy * 0.06) L += 0.08;                    // its lit left lip
        if (x < TM.x0 - 1 || x > TM.x1 + 1) L = 0.14 + (vnoise(x / 2, y / 6, 14) - 0.5) * 0.14 + (dy < 2 ? 0.12 : 0);   // the ends, unlit
        col = pick(ROCK, L, x, y);
      } else {
        const dc = y - cb;
        let v = 0.28 + 0.34 * fl * Math.exp(-dc / 7) + (vnoise(x / 2.2, y / 2, 15) - 0.5) * 0.3;   // talus lit under the cliffs, forest below
        const spur = Math.abs(frac((x + y * 0.55) / 23 + vnoise(x / 20, 0, 26) * 0.6) - 0.5);        // the ridges running down between ravines
        v += spur < 0.08 ? 0.16 : spur > 0.42 ? -0.12 : 0;
        const clump = vnoise(x / 1.8, y / 1.6, 27);                                                  // tree crowns: lit tops, dark undersides
        if (clump > 0.72) v += 0.14; else if (clump < 0.22) v -= 0.1;
        if (x < TM.x0 - 1 || x > TM.x1 + 1) v -= 0.1;
        col = pick(FOREST, v, x, y);
        if (Math.abs(y - (118.5 - (x - 150) * 0.018)) < 0.6 && x > 150 && x < 318 && hash(x >> 1, 4, 18) < 0.55) col = '#2a3038';   // Tafelberg Road's wall
      }
      if (x < TM.x0 || x > TM.x1) { if (dy === 0) col = '#3a3a5e'; else if (dy === 1 && y < cb) col = pick(ROCK, 0.3, x, y); }
      p.set(x, y, col);
    }
    // Lion's Head (the nearest): the bald dome of rock catching a little of the city's light, the shadow under its foot,
    // then the scrub of the neck and the dark shoulder
    const yl = Math.round(lhY(x)), al = Math.abs(x - LH.x), foot = LH.y + LH_DOME + LH_WALL;
    for (let y = Math.max(0, yl); y < ct; y++) {
      const dl = y - yl, head = al < LH_R + LH_WW + 0.5 && y < foot;
      let v;
      if (head) {
        v = 0.5 + 0.14 * (1 - al / (LH_R + LH_WW)) + (vnoise(x / 4, y / 3, 23) - 0.5) * 0.18 + (x < LH.x ? 0.06 : -0.08);   // smooth, rounded rock
        if (vnoise(x / 1.4, y / 6, 25) < 0.14) v -= 0.14;                    // a few joints down the walls
        if (y >= foot - 1) v -= 0.22;
      } else {
        v = 0.24 + (vnoise(x / 2.5, y / 3, 24) - 0.5) * 0.3;
        if (al < LH_R + LH_WW + 5 && y < foot + 3) v -= 0.12;                // the shadow under the dome's foot
      }
      if (dl === 0) v = 0.9; else if (dl === 1) v += 0.14;
      p.set(x, y, pick(PEAK, v, x, y));
    }
    // the neck pulled in a little under the dome's foot, so the bald cap overhangs it (in the picture only: lhY keeps the
    // outer profile for the stars and the hikers' path)
    if (al > LH_R - 1 && al < LH_R + LH_WW + 3) for (let y = LH.y + LH_DOME + 2; y < foot + 4; y++) {
      if (al > LH_R - 0.5 + 2.6 * smooth(foot - 1, foot + 4, y) && y >= yl) p.set(x, y, '#000000', 0);
    }
  }
  // the cableway: two cables from the upper station down to the lower one
  for (let k = 0; k <= 80; k++) {
    const s = k / 80, x = lerp(CAB.x0, CAB.x1, s), y = lerp(CAB.y0, CAB.y1, s) + Math.sin(Math.PI * s) * 2.5;
    if (k % 2 === 0) p.set(x, y, '#2a2638'); p.set(x + 1, y, (k & 3) ? '#3e3848' : '#2a2638');
  }
  // the upper station on the edge of the plateau, the lower one on Tafelberg Road
  p.fill(CAB.x0 - 6, CAB.y0 - 5, 11, 5, '#262236'); p.fill(CAB.x0 - 6, CAB.y0 - 6, 11, 1, '#3e3a50');
  p.fill(CAB.x0 - 2, CAB.y0 - 8, 4, 2, '#262236');
  for (const i of [-5, -3, -1, 1, 3]) p.set(CAB.x0 + i, CAB.y0 - 3, '#ffd690');
  p.fill(CAB.x1 - 5, CAB.y1 - 2, 11, 4, '#1c1e2a'); p.fill(CAB.x1 - 5, CAB.y1 - 3, 11, 1, '#343646');
  for (const i of [-4, -2, 0, 2, 4]) p.set(CAB.x1 + i, CAB.y1, i === 0 ? '#fff0c8' : '#ffc870');
  return trim(p);
}
/** the cabin's position on the cable at loop time t: two cabins on a jig-back, one up while the other comes down */
const cabS = s => [lerp(CAB.x0, CAB.x1, s), lerp(CAB.y0, CAB.y1, s) + Math.sin(Math.PI * s) * 2.5];
function cabinPhase(t) {
  const q = t % 120;                                  // 15 s at the stations, 40 s crossing, both ways each 2 minutes
  if (q < 15) return 1;
  if (q < 55) return 1 - smooth(15, 55, q);
  if (q < 75) return 0;
  if (q < 115) return smooth(75, 115, q);
  return 1;
}
/** hikers' headlamps coming down Lion's Head's zig-zag path after the sunset hike */
const LH_PATH = [[428, 77], [432, 79], [434, 82], [437, 88], [441, 95], [447, 98], [442, 100], [452, 102], [461, 105], [470, 110], [478, 113]];
const LH_LEN = LH_PATH.slice(1).reduce((s, q, i) => s + Math.hypot(q[0] - LH_PATH[i][0], q[1] - LH_PATH[i][1]), 0);
function pathAt(u) {
  let d = u * LH_LEN;
  for (let i = 1; i < LH_PATH.length; i++) {
    const [ax, ay] = LH_PATH[i - 1], [bx, by] = LH_PATH[i], l = Math.hypot(bx - ax, by - ay);
    if (d <= l) return [ax + (bx - ax) * d / l, ay + (by - ay) * d / l];
    d -= l;
  }
  return LH_PATH[LH_PATH.length - 1];
}
const HIKERS = [{ n: 3, ph: 0.1, col: '#f4f6ff' }, { n: 3, ph: 0.16, col: '#fff0c8' }, { n: 2, ph: 0.55, col: '#f4f6ff' }, { n: 4, ph: 0.8, col: '#ffe6b0' }];
const TAFEL_CARS = [{ t0: 36, t1: 64, x0: 316, x1: 150 }, { t0: 158, t1: 184, x0: 152, x1: 316 }];
function drawMountainFx(t) {
  // the lower station's lamps, the upper station glowing through the cloth
  halo(HL.station, CAB.x1, CAB.y1, 0.7);
  // cabins: a dark box with lit windows, hanging under the cable
  const sA = cabinPhase(t);
  for (const s of [sA, 1 - sA]) {
    const [x, y] = cabS(s), cx = Math.round(x), cy = Math.round(y);
    rect(ctx, cx, cy + 1, 1, 1, '#141220');
    rect(ctx, cx - 2, cy + 2, 5, 4, '#1c1a2a'); rect(ctx, cx - 1, cy + 3, 3, 2, '#ffd890'); rect(ctx, cx, cy + 3, 1, 1, '#fff4d8');
    halo(HL.cabin, cx, cy + 4, 0.6);
  }
  // headlamps on Lion's Head (they flicker as the hikers turn on the switchbacks)
  HIKERS.forEach((h, i) => {
    const u = frac(h.n * t / LOOP + h.ph);
    if (u > 0.94) return;
    const [x, y] = pathAt(u), on = hash(i, Math.floor(t * 3), 61) > 0.12;
    if (!on) return;
    rect(ctx, Math.round(x), Math.round(y), 1, 1, h.col); halo(HL.headlamp, x, y, 0.55);
  });
  // now and then a car winds along Tafelberg Road under the cliffs
  for (const c of TAFEL_CARS) {
    if (t < c.t0 || t >= c.t1) continue;
    const s = (t - c.t0) / (c.t1 - c.t0), x = lerp(c.x0, c.x1, s), y = 118 - (x - 150) * 0.018 - 1;
    const dir = Math.sign(c.x1 - c.x0);
    rect(ctx, Math.round(x), Math.round(y), 1, 1, '#fff4d8'); rect(ctx, Math.round(x) - dir * 2, Math.round(y), 1, 1, '#ff4a3a');
    halo(HL.carFar, x + dir * 2, y, 0.6);
  }
}

/* ================= The tablecloth: the south-easter's cloud, building on the flat top and clearing again ================= */
// The Cape Doctor drives moist air up the back of the mountain, where it condenses into a thin sheet lying on the flat top;
// it pours over the front edge in soft wisps that warm as they sink and dissolve within the upper half of the cliffs.
// Over the loop it builds (cloudlets drifting on the wind merge into one sheet, then it spills), lies through the middle
// and clears again, so the dead-flat top and its floodlit face stand bare for the minute around the loop's start. Painted
// per frame into a small buffer at four alpha levels picked by ordered dither, never opaque, so the rock and the top line
// show through; the texture scrolls down with the flow (TC_K periods per loop) and every wave has whole cycles per loop.
const TC = { x: 128, y: 40, w: 214, h: 64 };
const TC_P = 64, TC_K = 6;                            // texture period (rows), periods scrolled per loop (1.6 rows/s)
const TC_ON = 14, TC_OFF = 200, TC_FADE = 56;         // it builds over TC_ON .. +TC_FADE and clears over TC_OFF - TC_FADE .. TC_OFF
const clothAmount = t => env(t, TC_ON, TC_OFF, TC_FADE);
const LITTLE = new Uint8Array(Uint32Array.of(1).buffer)[0] === 1;
const pack = (h, a) => { const [r, g, b] = hex(h); return LITTLE ? ((a << 24) | (b << 16) | (g << 8) | r) >>> 0 : ((r << 24) | (g << 16) | (b << 8) | a) >>> 0; };
const TC_A = [56, 100, 144, 188];
const TC_COL = {                                      // the veil's four levels: cool where it lies in the dark, whiter in the floodlights
  cool: ['#a8acd2', '#c0c4e4', '#d6d9f2', '#e8eaf8'].map((h, k) => pack(h, TC_A[k])),
  lit: ['#dcd8e8', '#ebe8f2', '#f6f3f6', '#fffcf8'].map((h, k) => pack(h, TC_A[k])),
};
function vnoiseP(x, y, s, py) {                       // value noise, periodic in y every py lattice cells
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const y0 = ((yi % py) + py) % py, y1 = (y0 + 1) % py;
  const a = hash(xi, y0, s), b = hash(xi + 1, y0, s), c = hash(xi, y1, s), d = hash(xi + 1, y1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
let TCB;                                              // { tex, top, den, capH, lb, fl, img, u32, c, x }
function buildCloth() {
  const n = TC.w, tex = new Float32Array(n * TC_P);
  for (let y = 0; y < TC_P; y++) for (let i = 0; i < n; i++) {                // soft puffs of cloud, a little taller than wide
    const x = TC.x + i;
    tex[y * n + i] = 0.6 * vnoiseP(x / 7, y / 8, 91, 8) + 0.4 * vnoiseP(x / 3, y / 4, 92, 16);
  }
  const top = new Float32Array(n), den = new Float32Array(n), capH = new Float32Array(n), lb = new Float32Array(n), fl = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = TC.x + i;
    top[i] = Math.round(tmY(x));
    // the cloth lies from just past the eastern buttress to the western end, thinning out at both ends and over the station
    den[i] = smooth(134, 150, x) * (1 - smooth(320, 334, x)) * (1 - 0.4 * Math.exp(-(((x - (CAB.x0 - 1)) / 7) ** 2)));
    capH[i] = 3 + 1.8 * vnoise(x / 22, 0, 83);                                // a thin, level sheet on the top
    // how far down the face it reaches before it evaporates: broad, rounded lobes pouring down the bays, with only a short
    // spill between them
    lb[i] = (6 + 22 * smooth(0.3, 0.75, vnoise(x / 15, 0, 81)) + 4 * vnoise(x / 6, 0, 82)) * (0.6 + 0.4 * smooth(140, 180, x)) * (1 - 0.45 * smooth(300, 330, x));
    fl[i] = flood(x);
  }
  const [c, cx] = makeCanvas(n, TC.h), img = cx.createImageData(n, TC.h);
  TCB = { tex, top, den, capH, lb, fl, img, u32: new Uint32Array(img.data.buffer), c, x: cx };
}
function drawCloth(t) {
  const A = clothAmount(t);
  if (A < 0.004) return;
  const { tex, top, den, capH, lb, fl, u32, img, c, x: cx } = TCB, n = TC.w;
  const off = Math.floor(TC_K * TC_P * t / LOOP) % TC_P, g = gust(t);
  const capA = smooth(0, 0.5, A), pourA = smooth(0.12, 0.9, A);              // the sheet gathers on the top first, then spills
  u32.fill(0);
  for (let i = 0; i < n; i++) {
    if (den[i] <= 0.02) continue;
    const x = TC.x + i, ty = top[i] - TC.y;
    // cloudlets drifting downwind (to the right) merge into one sheet as the cloth builds, and break up again as it clears
    const patch = 0.55 + 0.27 * Math.sin(TAU * 2 * t / LOOP - x * 0.071 + 1.3) + 0.18 * Math.sin(TAU * 5 * t / LOOP - x * 0.163 + 0.4);
    const D = den[i] * clamp((patch + 1.45 * A - 1) / 0.35, 0, 1);
    if (D <= 0.02) continue;
    const ch = capA * D * (capH[i] + 0.6 * Math.sin(TAU * 3 * t / LOOP - x * 0.05));
    const Lp = pourA * D * (lb[i] * (0.8 + 0.2 * Math.sin(TAU * 4 * t / LOOP + x * 0.037)) + 4 * g);
    const y0 = Math.max(0, Math.floor(ty - ch)), y1 = Math.min(TC.h, Math.ceil(ty + Lp));
    for (let y = y0; y < y1; y++) {
      const by = y + TC.y;
      let d, pal = TC_COL.cool;
      if (y < ty) {                                                           // lying on the top, thinning upwards into the sky
        const h = (ty - y) / Math.max(1, ch);
        d = capA * D * (1.1 - h * h) * (0.75 + 0.45 * tex[((y * 3) % TC_P) * n + i]);
        if (fl[i] - h * 3 > bayer(by, x)) pal = TC_COL.lit;                  // its base catches the floodlights
      } else {                                                                // pouring over the edge, drifting downwind as it sinks
        const dy = y - ty, si = clamp(i - Math.round(dy * 0.25), 0, n - 1);
        const w = 0.5 + 0.55 * tex[(((y - off) % TC_P) + TC_P) % TC_P * n + si];          // thicker and thinner puffs sliding down
        d = pourA * D * ((1 - smooth(0.4, 1, dy / Lp)) * w + 0.15 * Math.exp(-dy / 2));   // an even body, a foot that dissolves
        if (fl[i] * 1.3 - 0.15 > bayer(by, x)) pal = TC_COL.lit;
      }
      const q = d * 4;
      let k = Math.floor(q);
      if (q - k > bayer(x, by)) k++;
      if (k > 0) u32[y * n + i] = pal[Math.min(k, 4) - 1];
    }
  }
  cx.putImageData(img, 0, 0);
  ctx.drawImage(c, TC.x, TC.y);
}

/* ================= The city bowl: Oranjezicht, Gardens and Tamboerskloof climbing the lower slopes ================= */
const CITY_LIGHT = ['#ffb454', '#ffb454', '#ffc46c', '#ffd89a', '#e8eeff', '#fff0d0', '#ffb454', '#dce6ff'];
const BLOCKS = [                                      // apartment blocks and offices in the Gardens [x, top, w, base]
  [124, 146, 11, 176], [161, 150, 9, 176], [206, 144, 13, 186], [226, 153, 8, 186], [258, 148, 11, 186], [286, 141, 10, 180],
  [320, 150, 9, 172], [343, 144, 8, 170],
];
const inTrees = (x, y) => vnoise(x / 9, y / 5, 101) > 0.68 || (x > 176 && x < 214 && y > 172 && vnoise(x / 4, y / 3, 102) > 0.35);
let CITY_TW = [], CITY_WIN = [];
function renderCity() {
  const p = pix(W, H), r = rng(111), lights = [];
  CITY_TW = []; CITY_WIN = [];
  for (let x = 0; x < W; x++) {
    const ct = cityTop(x);
    for (let y = ct; y < 200; y++) {
      const dd = y - ct;
      let v = 0.2 + smooth(0, 50, dd) * 0.28 + (vnoise(x / 2.5, y / 2, 103) - 0.5) * 0.3;
      if (inTrees(x, y)) v -= 0.18;
      if (dd === 0 && hash(x, 1, 104) < 0.6) v -= 0.12;                        // the ragged edge of the suburbs against the forest
      p.set(x, y, pick(['#0a0c18', '#0e1020', '#131426', '#18182c', '#1e1c30', '#262234'], v, x, y));
    }
  }
  // streets climbing towards Kloof Nek (converging up and right) and the contour streets across them
  const place = (x, y, sp) => { if (y < cityTop(x) + 1 || y > 199 || inTrees(x, y)) return; lights.push({ x: Math.round(x), y: Math.round(y), sp }); };
  for (let k = 0; k < 14; k++) {
    const x0 = -60 + k * 42 + r() * 18, vx = 430, vy = 112;
    for (let s = 0.06; s < 0.98; s += 0.035 + r() * 0.025) place(lerp(x0, vx, s), lerp(200, vy, s), true);
  }
  for (let k = 0; k < 16; k++) {
    const y0 = 124 + Math.pow(k / 15, 1.35) * 74, sl = -0.04 + r() * 0.08;
    for (let x = r() * 6; x < W; x += 3 + r() * 4 + (y0 < 140 ? 3 : 0)) place(x, y0 + (x - 240) * sl + Math.sin(x * 0.03 + k) * 1.5, true);
  }
  for (let k = 0; k < 900; k++) {                                               // houses' windows scattered between the streets
    const x = r() * W, y = 122 + Math.pow(r(), 0.7) * 76;
    if (r() < 0.55 + (y - 122) / 160) place(x, y, false);
  }
  for (const L of lights) {
    const v = r();
    const col = L.sp ? CITY_LIGHT[Math.floor(r() * CITY_LIGHT.length)] : v < 0.7 ? (r() < 0.6 ? '#ffcf8a' : '#ffe6b8') : v < 0.8 ? '#9ab8ff' : '#e0d0a8';
    const far = L.y < 140;
    if (L.sp && r() < 0.28) { CITY_TW.push({ x: L.x, y: L.y, col, k: 30 + Math.floor(r() * 90), ph: r(), a: far ? 0.7 : 0.9 }); continue; }
    if (!L.sp && r() < 0.12) { CITY_WIN.push({ id: CITY_WIN.length, x: L.x, y: L.y, col, n: 2 + Math.floor(r() * 6), off: r() }); continue; }
    p.set(L.x, L.y, far && r() < 0.5 ? mixHex(col, '#2a2440', 0.35) : col);
    if (L.sp && !far && r() < 0.35) p.set(L.x + 1, L.y, mixHex(col, '#1a1828', 0.5));
  }
  // apartment blocks: flat roofs, grids of windows (some lit), a red light on the tallest
  for (const [bx, top, w, base] of BLOCKS) {
    for (let y = top; y < base; y++) for (let x = bx; x < bx + w; x++) {
      const fr = x === bx + w - 1 || x === bx + w - 2 && w > 9;
      p.set(x, y, y === top ? '#34304a' : fr ? '#14121e' : pick(['#16151f', '#1c1a28', '#221f2e'], 0.5 + (y - top) / 60, x, y));
    }
    for (let y = top + 2; y < base - 1; y += 3) for (let x = bx + 1; x < bx + w - 2; x += 2) {
      const v = r();
      if (v < 0.42) p.set(x, y, v < 0.08 ? '#9ab8ff' : v < 0.3 ? '#ffd28e' : '#ffe8c0');
      else if (v < 0.5) CITY_WIN.push({ id: CITY_WIN.length, x, y, col: '#ffd28e', n: 2 + Math.floor(r() * 5), off: r() });
    }
  }
  return trim(p);
}
const CITY_CARS = [                                   // tail and head lights crawling along the streets towards Kloof Nek
  { k: 3, ph: 0.1, x0: 120, dir: 1 }, { k: 2, ph: 0.45, x0: 204, dir: -1 }, { k: 4, ph: 0.7, x0: 36, dir: 1 }, { k: 3, ph: 0.3, x0: 288, dir: -1 },
  { k: 2, ph: 0.85, x0: 160, dir: 1 },
];
function drawCity(t) {
  blit(CITY_C);
  for (const s of CITY_TW) { ctx.globalAlpha = s.a * (0.55 + 0.45 * wave(t, s.k, s.ph)); rect(ctx, s.x, s.y, 1, 1, s.col); }
  ctx.globalAlpha = 1;
  for (const w of CITY_WIN) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 105) < 0.55) rect(ctx, w.x, w.y, 1, 1, w.col);
  for (const c of CITY_CARS) {
    const u = frac(c.k * t / LOOP + c.ph), s = c.dir > 0 ? 0.1 + u * 0.55 : 0.65 - u * 0.55;
    const x = Math.round(lerp(c.x0, 430, s)), y = Math.round(lerp(200, 112, s));
    if (y < cityTop(x) + 2 || y > 196) continue;
    rect(ctx, x, y, 1, 1, c.dir > 0 ? '#ff4a3a' : '#fff2d0');
  }
  if (frac(t / 2.4) < 0.4) { rect(ctx, 290, 140, 1, 1, '#ff3a3a'); halo(HL.red, 290, 140, 0.5); }
}

/* ================= Palms in the city, tossed by the south-easter (crowns pre-rendered at seven leans) ================= */
const PALMS = [{ x: 150, base: 162, h: 32, s: 0.8 }, { x: 263, base: 188, h: 38, s: 1 }, { x: 305, base: 174, h: 30, s: 0.85 }];
const PALM_LEANS = 7;
function renderPalmCrown(lean, sc) {
  const s = Math.round(26 * sc), p = pix(s * 2 + 1, s + 6), cx = s, cy = 5;
  const fronds = 9;
  for (let k = 0; k < fronds; k++) {
    let a = -Math.PI / 2 + (k / (fronds - 1) - 0.5) * 3.3;
    a += lean * 0.55;                                                        // everything swept downwind (to the right)
    const len = s * (0.45 + 0.35 * Math.sin(Math.PI * k / (fronds - 1))) * (1 + 0.15 * lean * Math.cos(a));
    for (let q = 0; q <= len; q++) {
      const u = q / len, droop = u * u * len * 0.45 * (1 - 0.3 * lean);
      const x = cx + Math.cos(a) * q, y = cy + Math.sin(a) * q + droop;
      p.set(x, y, u < 0.5 ? '#16241c' : '#1e3024');
      p.set(x, y - 1, u > 0.2 && u < 0.8 ? '#2e4434' : '#1a2a20');                     // the sky's rim on top of the fronds
      if (u > 0.25 && q % 2 === 0) { p.set(x, y + 1, '#0e1a14'); if (sc > 0.75) p.set(x + 0.7, y + 1.6, '#3a3024'); }   // leaflets, lit warm from below
    }
  }
  p.fill(cx - 1, cy - 1, 3, 3, '#141c16');
  return p.done();
}
function drawPalms(t) {
  const g = gust(t);
  PALMS.forEach((pm, i) => {
    const lean = clamp(0.35 + 0.25 * g + (0.08 + 0.18 * g) * wave(t, 37 + i * 5, i * 0.3) + 0.06 * wave(t, 97 + i * 11, i * 0.7), 0, 0.99);
    const f = Math.round(lean * (PALM_LEANS - 1)), spr = PALM_C[i][f];
    const topX = pm.x + Math.round(lean * 3 * pm.s), topY = pm.base - pm.h;
    for (let y = topY; y < pm.base; y++) {
      const u = (pm.base - y) / pm.h, x = Math.round(pm.x + (topX - pm.x) * u * u);
      ctx.fillStyle = '#0e1210'; ctx.fillRect(x, y, 1, 1); ctx.fillStyle = (y & 3) ? '#3a2e24' : '#241c18'; ctx.fillRect(x + 1, y, 1, 1);   // the city's light on the trunk
    }
    ctx.drawImage(spr, topX - (spr.width >> 1), topY - 5);
  });
}

/* ================= The minaret of a Bo-Kaap mosque, beyond the cross street ================= */
const MX = 197, MTOP = 113;
function renderMinaret() {
  const p = pix(W, H);
  const white = (x, y, lv) => p.set(x, y, pick(['#4a4458', '#7a7288', '#a8a0b0', '#d0c8cc', '#ece4de'], lv + (hash(x, y, 120) - 0.5) * 0.1, x, y));
  for (let y = 132; y < 190; y++) for (let x = MX - 2; x <= MX + 2; x++) {  // the shaft, lit warm from the street below
    let lv = 0.5 + (x - MX) * -0.1 + smooth(150, 190, y) * 0.2;
    if (x === MX + 2) lv -= 0.25;
    if ((y - 132) % 14 === 0) lv += 0.2;                                     // moulded bands
    if (y > 140 && y < 146 && x === MX) lv = -1;                             // a slit window
    if (lv < 0) p.set(x, y, '#2a2230'); else white(x, y, lv);
  }
  for (let x = MX - 4; x <= MX + 4; x++) { white(x, 131, 0.75); p.set(x, 130, '#2a2638'); white(x, 129, 0.6); }   // the balcony
  for (let x = MX - 4; x <= MX + 4; x += 2) white(x, 128, 0.5);                                                   // its railing
  for (let y = 121; y < 128; y++) for (let x = MX - 1; x <= MX + 1; x++) white(x, y, 0.55 - (x - MX) * 0.15);   // the upper stage
  for (let x = MX - 2; x <= MX + 2; x++) white(x, 120, 0.7);
  for (let y = MTOP + 2; y < 120; y++) {                                                                        // the pointed dome
    const hw = Math.round((y - MTOP - 1) * 0.36);
    for (let x = MX - hw; x <= MX + hw; x++) p.set(x, y, x > MX ? '#2e5a44' : '#4a8a64');
  }
  p.set(MX, MTOP + 1, '#c8b070'); p.set(MX, MTOP, '#c8b070'); p.set(MX + 1, MTOP - 1, '#e0c880'); p.set(MX, MTOP - 2, '#e0c880'); p.set(MX - 1, MTOP - 1, '#e0c880');   // crescent
  return trim(p);
}
function drawMinaret(t) {
  blit(MIN_C);
  const g = 0.75 + 0.25 * wave(t, 12, 0.3);                                 // the green lights round the balcony and the dome
  ctx.globalAlpha = g; rect(ctx, MX - 4, 131, 9, 1, '#6aff9a'); ctx.globalAlpha = 1;
  for (let k = -4; k <= 4; k += 2) rect(ctx, MX + k, 128, 1, 1, g > 0.8 ? '#baffcc' : '#6aff9a');
  halo(HL.green, MX, 130, g * 0.8); halo(HL.greenTop, MX, MTOP + 4, 0.5 + 0.2 * g);
}

/* ================= Gulls wheeling over the city, lit from below ================= */
const GULLS = [{ k: 4, ph: 0.1, cx: 150, cy: 112, rx: 40, ry: 8, dir: 1 }, { k: 5, ph: 0.6, cx: 292, cy: 104, rx: 34, ry: 7, dir: -1 },
  { k: 3, ph: 0.35, cx: 226, cy: 124, rx: 52, ry: 6, dir: 1 }];
const GULL_F = [['#...#', '.#.#.', '..#..'], ['##.##', '..#..'], ['..#..', '.#.#.', '#...#']];
function drawGulls(t) {
  GULLS.forEach((g, i) => {
    const a = TAU * (g.k * t / LOOP + g.ph) * g.dir;
    const x = Math.round(g.cx + Math.cos(a) * g.rx + gustPush(t) * 0.0), y = Math.round(g.cy + Math.sin(a) * g.ry + wave(t, 29 + i * 7, i * 0.3) * 2);
    const flap = frac(t * 1.3 + i * 0.37), fr = flap < 0.5 ? 1 : flap < 0.68 ? 0 : flap < 0.84 ? 2 : 1;
    ctx.fillStyle = y > 110 ? '#f0dcc4' : '#dcd8e8';
    GULL_F[fr].forEach((row, j) => { for (let q = 0; q < 5; q++) if (row[q] === '#') ctx.fillRect(x - 2 + q, y - 1 + j, 1, 1); });
  });
}

/* ================= The billboard: a painted ad on a gable wall, for a made-up bakery's Sunday koesisters ================= */
// Bo-Kaap's koesisters: spiced, syrup-dipped doughnuts rolled in coconut. The wall belongs to a tall old building at the
// foot of the hill; without the billboard the wall stays, bare, with its two small vents.
const WALLR = ['#141220', '#1c1826', '#26202e', '#322a36', '#40363e', '#504448', '#625450', '#766658', '#8c7a64'];
function renderGableWall(board) {
  const p = pix(W, H);
  for (let y = BBW.top; y < 172; y++) for (let x = BBW.x0; x < BBW.x1; x++) {
    let v = 0.2 + (vnoise(x / 6, y / 6, 131) - 0.5) * 0.14 + (vnoise(x / 2, y / 2, 132) - 0.5) * 0.06 + smooth(150, 172, y) * 0.1;
    if (board) v += 0.42 * Math.max(0, 1 - Math.hypot((x - BB.x - BB.w / 2) / 40, (y - BB.y - 6) / 24));   // the lamps' spill round the ad
    if (x >= BBW.x1 - 3) v -= 0.12;                                          // the corner turning away
    if (y < BBW.top + 2) v = y === BBW.top ? 0.55 : 0.1;                      // coping and its shadow
    if (y > 150 && (y - 150) % 9 === 0) v -= 0.08;                            // old floor lines showing through the plaster
    p.set(x, y, pick(WALLR, v, x, y));
  }
  if (!board) [[336, 119, 0], [350, 119, 1], [364, 119, 0], [343, 131, 0], [357, 131, 1]].forEach(([vx, vy, lit]) => {   // the flats' small side windows
    p.fill(vx, vy, 4, 5, lit ? '#e8a454' : '#0e0c16'); if (lit) p.fill(vx, vy, 4, 1, '#ffd08a'); p.fill(vx - 1, vy + 5, 6, 1, '#4a3e44');
  });
  if (board) {
    for (let x = BB.x - 1; x <= BB.x + BB.w; x++) { p.set(x, BB.y - 1, '#2a2230'); p.set(x, BB.y + BB.h, '#1a1620'); }   // the painted border
    for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { p.set(BB.x - 1, y, '#2a2230'); p.set(BB.x + BB.w, y, '#1a1620'); }
    for (const lx of BB_LAMPS) {                                              // gooseneck lamps reaching out over the ad
      const X = BB.x + lx;
      p.fill(X, BBW.top - 3, 1, 3, '#1c1a24'); p.fill(X - 3, BBW.top - 4, 4, 1, '#24222c'); p.fill(X - 4, BBW.top - 3, 1, 2, '#24222c');
      p.fill(X - 5, BBW.top - 1, 3, 1, '#34303a');
    }
  }
  return trim(p);
}
const AD_BG = ['#150a24', '#1f0f34', '#2a1444', '#361a54', '#442064', '#542874', '#663284'];
function pickFlat(ramp, v, i, j) {                    // a posterised ramp: flat bands with a narrow dither seam (paint, not a gradient)
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * 0.3) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
const KOE = { x: 7, y: 8, rx: 5.6, ry: 3.3 };         // the koesister on the ad (face-local)
function renderAd() {
  const p = pix(BB.w, BB.h), m = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    const v = 0.14 + BB_LAMPS.reduce((s, lx) => s + 0.5 * Math.max(0, 1 - Math.hypot((i - lx) / 18, j / 22)), 0);
    p.set(i, j, pickFlat(AD_BG, v, i, j));
  }
  for (let j = 11; j < 14; j++) for (let i = 1; i < 14; i++) {              // a white plate
    const e = ((i - 7) / 6.4) ** 2 + ((j - 12) / 1.6) ** 2;
    if (e <= 1) p.set(i, j, j === 11 ? '#fffaf0' : e > 0.6 ? '#9a94a8' : '#e8e4ec');
  }
  for (let j = 4; j < 12; j++) for (let i = 0; i < 15; i++) {                // the koesister: golden, lit from the lamps above
    const dx = (i - KOE.x) / KOE.rx, dy = (j - KOE.y) / KOE.ry, e = dx * dx + dy * dy;
    if (e > 1) continue;
    const lv = 0.62 - dy * 0.35 - dx * 0.12 - e * 0.2;
    p.set(i, j, pickFlat(['#4a200c', '#7a3c16', '#a85e26', '#cc8036', '#e8a64a', '#f8cc70'], lv, i, j));
    m.set(i, j, '#ffffff');
    if (hash(i, j, 141) < (dy < 0.2 ? 0.34 : 0.12)) p.set(i, j, dy < -0.3 ? '#fffaf0' : '#f0e6d4');   // coconut
  }
  const txt = (s, x, y, sc, col, sh) => { if (sh) textP(p, s, x + 1, y + 1, sh, sc); textP(p, s, x, y, col, sc); textP(m, s, x, y, '#ffffff', sc); };
  txt('SOET', 15, 1, 2, '#ff6ab4', '#1a0620');
  txt('KOESISTERS', 4, 14, 1, '#e8f55c', '#140820');
  return { c: p.done(), mask: m.done() };
}
const BB_FLICK = [[71.3, 72.6], [188.2, 189.1]];      // the right lamp stutters
function drawBillboard(t, pitch) {
  const hum = 0.95 + 0.05 * wave(t, 1500, 0.2), off = inEvent(t, BB_FLICK) && hash(77, Math.floor(t * 18), 2) < 0.5;
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + 6, (off ? 0.7 : 1) * hum);
  if (!BB_PITCH.covers(t, pitch)) {
    ctx.drawImage(AD.c, BB.x, BB.y);
    for (let k = 0; k < 3; k++) {                                            // steam curling up off the koesister
      const u = frac(t / 3.2 + k / 3), x = BB.x + KOE.x - 2 + k * 2 + Math.round(Math.sin(u * 5 + k) * 1.2), y = BB.y + 4 - Math.round(u * 4);
      ctx.globalAlpha = 0.55 * Math.sin(Math.PI * u); rect(ctx, x, y, 1, 1, '#f4ecff');
    }
    ctx.globalAlpha = 1;
    const sp = step(t, 160);                                                 // syrup and coconut glinting
    if (hash(sp, 3, 142) < 0.5) { const q = Math.floor(hash(sp, 4, 142) * 5); rect(ctx, BB.x + [4, 7, 10, 6, 9][q], BB.y + [6, 5, 7, 8, 9][q], 1, 1, '#ffffff'); }
    const f = frac(t / 18);                                                  // a gloss sheen rolls over the paint
    if (f < 0.1) {
      const bx = lerp(-10, BB.w + 10, f / 0.1);
      for (let j = 0; j < BB.h; j++) {
        const sx = Math.round(bx - j * 0.6), a = Math.max(0, sx), b = Math.min(BB.w, sx + 3);
        if (b <= a) continue;
        ctx.globalAlpha = 0.12; rect(ctx, BB.x + a, BB.y + j, b - a, 1, '#ffffff');
        ctx.globalAlpha = 0.5; ctx.drawImage(AD.mask, a, j, b - a, 1, BB.x + a, BB.y + j, b - a, 1);
      }
      ctx.globalAlpha = 1;
    }
    if (off) { ctx.globalAlpha = 0.35; rect(ctx, BB.x + 24, BB.y, 22, BB.h, '#060416'); ctx.globalAlpha = 1; }
  }
  BB_PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {
    if (off && k === 1) return;
    rect(ctx, BB.x + lx - 5, BBW.top, 3, 1, '#fff4dc');
    halo(HL.bbLamp, BB.x + lx - 4, BBW.top + 1, hum);
  });
}

/* ================= Bo-Kaap: the lane's terraced cottages, stepping down the hill ================= */
const PINK = ['#240e20', '#3e1634', '#5c2048', '#7e2c5e', '#a23c76', '#c4508c', '#e070a6', '#f698c0'];
const LIME = ['#141c10', '#223016', '#34481c', '#4a6422', '#62822a', '#80a232', '#a2c040', '#c4dc62'];
const BLUE = ['#0c1428', '#142242', '#1c3260', '#264680', '#325ea0', '#4478bc', '#5e96d4', '#88b8e8'];
const YELLOW = ['#241a0e', '#3c2c12', '#5a4218', '#7c5c1e', '#a07a24', '#c29a2c', '#dcba40', '#f0d668'];
const VIOLET = ['#170f28', '#241840', '#34225a', '#462e76', '#5a3c92', '#7250ac', '#8e6cc4', '#ae92da'];
const ORANGE = ['#241208', '#3e1e0e', '#5c2c12', '#7e3e18', '#a2521e', '#c26a28', '#de8a3a', '#f2ae5c'];
const TURQ = ['#081c20', '#0e2e34', '#14444a', '#1c5e62', '#267a7a', '#349892', '#4cb6aa', '#78d4c6'];
const CORAL = ['#240c0e', '#3e1418', '#5c1e22', '#7e2a2c', '#a23a36', '#c24e42', '#dc6a54', '#f08e6e'];
const HOUSES = [
  { side: -1, za: 7.2, zb: 13.2, h: 4.6, ramp: PINK, door: '#1c5a4e', raised: 1, stoep: 1 },
  { side: -1, za: 13.2, zb: 19.2, h: 5.2, ramp: LIME, door: '#6a2436', stoep: 1 },
  { side: -1, za: 19.2, zb: 25.2, h: 5.2, ramp: BLUE, door: '#e0d8c8', raised: 1, stoep: 1 },
  { side: -1, za: 25.2, zb: 31.2, h: 5.2, ramp: YELLOW, door: '#2a4a8a', stoep: 1 },
  { side: -1, za: 31.2, zb: 37.2, h: 5.2, ramp: VIOLET, door: '#e8c850', raised: 1 },
  { side: -1, za: 37.2, zb: 43.2, h: 7.8, ramp: ORANGE, door: '#1e3a5a' },
  { side: -1, za: 43.2, zb: 50, h: 5.2, ramp: TURQ, door: '#8a2a2a' },
  { side: 1, za: 8.2, zb: 14.2, h: 4.6, ramp: TURQ, door: '#f0e6d0', raised: 1, stoep: 1, open: 1, bench: 1 },
  { side: 1, za: 14.2, zb: 20.2, h: 5.2, ramp: YELLOW, door: '#3a2a6a', stoep: 1 },
  { side: 1, za: 20.2, zb: 26.2, h: 5.2, ramp: CORAL, door: '#1e4a3a', raised: 1, stoep: 1 },
  { side: 1, za: 26.2, zb: 32.2, h: 5.2, ramp: PINK, door: '#1c3a6a' },
  { side: 1, za: 32.2, zb: 38.2, h: 7.8, ramp: LIME, door: '#5a2a1a', raised: 1 },
  { side: 1, za: 38.2, zb: 44.2, h: 5.2, ramp: VIOLET, door: '#e0d0b0' },
  { side: 1, za: 44.2, zb: 50, h: 5.2, ramp: BLUE, door: '#7a3a1a' },
];
const DOOR_SH = {};
HOUSES.forEach((hs, i) => { DOOR_SH[hs.door] = [mixHex(hs.door, '#000000', 0.35), mixHex(hs.door, '#000000', 0.62)]; hs.id = i; hs.lat = hs.side < 0 ? LFAC : RFAC; hs.b = ground(hs.za); hs.w = hs.zb - hs.za; });
// the lamps that light the lane (3D, metres): wall lanterns on the first houses, a lamp post at the bottom
const LAMPS = [
  { lat: LFAC + 0.25, z: 10.3, alt: ground(7.2) + 2.75, k: 0.55, r: 6.5 },
  { lat: RFAC - 0.25, z: 11.6, alt: ground(8.2) + 2.75, k: 0.52, r: 6.5 },
  { lat: -3.9, z: 38, alt: ground(38) + 4.6, k: 0.72, r: 12 },
];
LAMPS.forEach(l => { [l.x, l.y] = proj(l.lat, l.z, l.alt); });
const lightAt = (lat, z, alt) => LAMPS.reduce((s, l) => s + l.k * Math.pow(Math.max(0, 1 - Math.hypot(lat - l.lat, z - l.z, alt - l.alt) / l.r), 1.6), 0);
/** the features on a cottage's front, in metres along it (d) and up from its floor (h) */
function features(hs) {
  const w = hs.w, m = w / 2, f = [];
  f.push({ k: 'win', d0: 0.8, d1: 2.0, h0: 0.95, h1: 2.4 }, { k: 'door', d0: m - 0.5, d1: m + 0.5, h0: 0, h1: 2.25 },
    { k: 'fan', d0: m - 0.5, d1: m + 0.5, h0: 2.32, h1: 2.72 }, { k: 'win', d0: w - 2.0, d1: w - 0.8, h0: 0.95, h1: 2.4 });
  if (hs.h > 7) f.push({ k: 'win', d0: 0.8, d1: 2.0, h0: 3.6, h1: 5.0 }, { k: 'win', d0: m - 0.6, d1: m + 0.6, h0: 3.6, h1: 5.0 }, { k: 'win', d0: w - 2.0, d1: w - 0.8, h0: 3.6, h1: 5.0 });
  return f;
}
const raisedTop = (d, w) => { const u = (d - (w / 2 - 1.1)) / 2.2; return u > 0 && u < 1 ? 0.55 * Math.pow(Math.sin(Math.PI * u), 0.6) : 0; };
const plaster = (x, y, s) => (vnoise(x / 5, y / 5, s) - 0.5) * 0.09 + (vnoise(x / 1.6, y / 1.6, s + 1) - 0.5) * 0.04;
let WINS = [];                                        // lit overlays of every window and fanlight: { c, x, y, id, n, off, p, cx, cy, r }
function paintFacade(p, hs) {
  const K = F * Math.abs(hs.lat), b = hs.b, feats = features(hs), rec = [];
  for (const ft of feats) {                                                  // each window gets its own lit overlay buffer
    if (ft.k === 'door' && !hs.open) continue;
    const pts = [[ft.d0, ft.h0], [ft.d1, ft.h0], [ft.d0, ft.h1], [ft.d1, ft.h1]].map(([d, h]) => proj(hs.lat, hs.za + d, b + h));
    const bx = Math.floor(Math.min(...pts.map(q => q[0]))) - 1, by = Math.floor(Math.min(...pts.map(q => q[1]))) - 1;
    const bw = Math.ceil(Math.max(...pts.map(q => q[0]))) - bx + 2, bh = Math.ceil(Math.max(...pts.map(q => q[1]))) - by + 2;
    ft.ov = pix(Math.max(1, bw), Math.max(1, bh)); ft.bx = bx; ft.by = by; ft.any = false;
    rec.push(ft);
  }
  const x0 = Math.floor(VPX + hs.side * K / (hs.side < 0 ? hs.za : hs.zb)), x1 = Math.ceil(VPX + hs.side * K / (hs.side < 0 ? hs.zb : hs.za));
  for (let x = Math.max(0, x0); x < Math.min(W, x1 + 1); x++) {
    const z = K / Math.abs(x + 0.5 - VPX);
    if (z < hs.za || z > hs.zb) continue;
    const d = z - hs.za, top = hs.h + (hs.raised ? raisedTop(d, hs.w) : 0);
    const yTop = Math.round(HZ - F * (b + top) / z), yBot = Math.round(HZ - F * ground(z) / z);
    for (let y = Math.max(0, yTop); y <= Math.min(H - 1, yBot); y++) {
      const alt = (HZ - (y + 0.5)) * z / F, hl = alt - b, L = lightAt(hs.lat, z, alt);
      let v = 0.2 + L * 0.95 + plaster(x, y, 150 + hs.id * 3) - 0.05 * smooth(3, 5.2, hl);
      let col = null;
      if (hl < 0.38) v -= 0.14 + (hl < 0 ? 0.05 : 0);                       // the plinth, painted darker
      else if (hl > hs.h - 0.62 && hl < hs.h - 0.44) v += 0.16;              // the moulded cornice
      else if (hl >= hs.h - 0.44 && hl < hs.h - 0.36) v -= 0.2;              // its shadow line
      if (hl > top - 0.14) v += 0.22;                                        // the lit coping
      if (d < 0.12 || hs.w - d < 0.12) v -= 0.08;                            // the joint between cottages
      for (const ft of feats) {
        if (d < ft.d0 - 0.1 || d > ft.d1 + 0.1 || hl < ft.h0 - 0.12 || hl > ft.h1 + 0.1) continue;
        const inD = d >= ft.d0 && d <= ft.d1 && hl >= ft.h0 && hl <= ft.h1;
        if (!inD) { col = pick(['#5a5450', '#8a847c', '#c4beb4', '#ece6dc'], 0.3 + L * 1.2, x, y); break; }   // white-painted surround
        const u = (d - ft.d0) / (ft.d1 - ft.d0), q = (hl - ft.h0) / (ft.h1 - ft.h0);
        if (ft.k === 'door') {
          if (hs.open) { col = pick(['#b86a2a', '#e8963e', '#ffc070', '#ffe0a8'], 0.95 - q * 0.5, x, y); if (q > 0.1 && q < 0.72 && u > 0.35 && u < 0.62) col = '#5a2e1a'; }
          else { const pan = (u > 0.14 && u < 0.44 || u > 0.56 && u < 0.86) && (q > 0.08 && q < 0.44 || q > 0.54 && q < 0.9);
            const lv = (pan ? 0.3 : 0.62) + L * 0.7; col = lv > 0.75 ? hs.door : lv > 0.45 ? DOOR_SH[hs.door][0] : DOOR_SH[hs.door][1]; }
          break;
        }
        const bar = ft.k === 'fan' ? Math.abs(u - 0.5) < 0.06 || Math.abs(u - 0.25) < 0.05 || Math.abs(u - 0.75) < 0.05
          : Math.abs(q - 0.5) < 0.05 || Math.abs(q - 0.25) < 0.04 || Math.abs(q - 0.75) < 0.04 || Math.abs(u - 0.33) < 0.05 || Math.abs(u - 0.67) < 0.05;
        if (bar) col = pick(['#4a4642', '#7a746c', '#b0aaa0'], 0.3 + L, x, y);
        else col = pick(['#0e1020', '#141628', '#1c2036', '#282c48'], 0.3 + (1 - q) * 0.35 + (hash(x, y, 160) < 0.1 ? 0.3 : 0), x, y);   // dark glass, the sky caught in it
        if (ft.ov) {
          const lc = bar ? '#7a4a2c' : ft.k === 'fan' ? '#ffc878' : u < 0.16 || u > 0.84 ? '#ffe2b0' : q > 0.72 ? '#ffd08a' : '#f4a650';
          ft.ov.set(x - ft.bx, y - ft.by, lc); ft.any = true;
        }
        break;
      }
      p.set(x, y, col || pick(hs.ramp, v, x, y));
    }
  }
  const r = rng(700 + hs.id);
  for (const ft of rec) {
    if (!ft.any) continue;
    const c = ft.ov.done(), cx = ft.bx + ft.ov.w / 2, cy = ft.by + ft.ov.h / 2, open = hs.open && ft.k === 'door';
    WINS.push({ c, x: ft.bx, y: ft.by, id: WINS.length, n: 2 + Math.floor(r() * 6), off: r(), p: open ? 2 : ft.k === 'fan' ? 0.5 : 0.62, cx, cy, big: ft.ov.w > 8 });
  }
}
/** a stoep: a raised platform in front of the door, a low wall along its edge (open where people sit on it) */
function paintStoep(p, hs) {
  const s = hs.side, latF = hs.lat, latS = latF - s * 1.35, z0 = hs.za + hs.w / 2 - 1.3, z1 = hs.za + hs.w / 2 + 1.3, top = hs.b + 0.03;
  const shade = (base, extra = 0) => (x, y) => {
    const z = (z0 + z1) / 2, L = lightAt(latS, z, top);
    return pick(hs.ramp, base - 0.08 + L * 0.9 + extra + plaster(x, y, 170), x, y);
  };
  quad3(p, [[latS, z0, ground(z0)], [latF, z0, ground(z0)], [latF, z0, top], [latS, z0, top]], shade(0.34));      // its near end
  quad3(p, [[latS, z0, ground(z0)], [latS, z1, ground(z1)], [latS, z1, top], [latS, z0, top]], shade(0.22));      // its side along the lane
  quad3(p, [[latS, z0, top], [latF, z0, top], [latF, z1, top], [latS, z1, top]], (x, y) => pick(['#3a2a2a', '#5a3c34', '#7a5040', '#9a664c'], 0.3 + lightAt(latS, z0, top) + ((x + y) & 1) * 0.04, x, y));   // red-polished floor
  if (hs.bench) {                                                            // a bench against the wall
    quad3(p, [[latF - s * 0.45, z0 + 0.3, top + 0.45], [latF, z0 + 0.3, top + 0.45], [latF, z1 - 0.3, top + 0.45], [latF - s * 0.45, z1 - 0.3, top + 0.45]], () => '#5a3a26');
    quad3(p, [[latF - s * 0.45, z0 + 0.3, top], [latF - s * 0.45, z1 - 0.3, top], [latF - s * 0.45, z1 - 0.3, top + 0.45], [latF - s * 0.45, z0 + 0.3, top + 0.45]], () => '#2a1c16');
    return;
  }
  const wall = (za, zb) => {
    const wl = latS + s * 0.22, wt = top + 0.45;
    quad3(p, [[latS, za, top], [wl, za, top], [wl, za, wt], [latS, za, wt]], shade(0.5));
    quad3(p, [[latS, za, top], [latS, zb, top], [latS, zb, wt], [latS, za, wt]], shade(0.4));
    quad3(p, [[latS, za, wt], [wl, za, wt], [wl, zb, wt], [latS, zb, wt]], (x, y) => pick(['#6a6460', '#a49c92', '#dcd4c8'], 0.3 + lightAt(latS, za, wt) * 1.2, x, y));   // white coping
  };
  const zm = (z0 + z1) / 2;
  wall(z0, zm - 0.45); wall(zm + 0.45, z1);
}
/* ================= The houses across the cross street, facing up the lane ================= */
const FAR = [
  { a: -16, b: -9.6, h: 5.2, ramp: TURQ, door: '#e0d0b0' }, { a: -9.6, b: -3.4, h: 5.2, ramp: LIME, door: '#6a2436' },
  { a: -3.4, b: 2.6, h: 7.8, ramp: PINK, door: '#1c4a60' }, { a: 2.6, b: 8.6, h: 5.2, ramp: YELLOW, door: '#2a4a2a', cafe: 1 },
  { a: 8.6, b: 15, h: 5.2, ramp: BLUE, door: '#e8d8b8' },
];
const FAR_B = ground(CROSS.z1);
function paintFar(p) {
  const z = FAR_Z, sc = F / z;
  for (const fh of FAR) {
    const xa = Math.round(VPX + fh.a * sc), xb = Math.round(VPX + fh.b * sc), yt = Math.round(HZ - (FAR_B + fh.h) * sc), yb = Math.round(HZ - FAR_B * sc);
    const w = fh.b - fh.a;
    for (let x = xa; x < xb; x++) for (let y = yt; y <= yb; y++) {
      const lat = (x + 0.5 - VPX) / sc, d = lat - fh.a, hl = (HZ - (y + 0.5)) / sc - FAR_B;
      const L = lightAt(lat, z, hl + FAR_B) + 0.12 * Math.max(0, 1 - Math.abs(lat - 1) / 10);
      let v = 0.24 + L * 0.9 + plaster(x, y, 180);
      if (hl < 0.4) v -= 0.14;
      if (hl > fh.h - 0.6 && hl < fh.h - 0.4) v += 0.16;
      if (y === yt) v += 0.2;
      let col = pick(fh.ramp, v, x, y);
      if (fh.cafe && hl > 0.3 && hl < 2.5 && d > 0.6 && d < 3.8) col = pick(['#c87a3a', '#f0a850', '#ffd08a'], 0.4 + (2.5 - hl) * 0.25 + (hash(x, y, 181) < 0.15 ? -0.3 : 0), x, y);   // the café's shopfront
      else if (fh.cafe && hl > 2.45 && hl < 3.75 && d > 0.4 && d < 4.1) col = '#7a1a1e';                        // its fascia
      else if (hl > 0.95 && hl < 2.4 && ((d > 0.9 && d < 2.1) || (d > w - 2.1 && d < w - 0.9)) && !fh.cafe) col = '#161a2c';
      else if (fh.h > 7 && hl > 3.6 && hl < 5 && ((d > 0.9 && d < 2.1) || (d > w / 2 - 0.6 && d < w / 2 + 0.6) || (d > w - 2.1 && d < w - 0.9))) col = '#161a2c';
      else if (hl < 2.25 && Math.abs(d - (fh.cafe ? 4.8 : w / 2)) < 0.5) col = fh.door;
      p.set(x, y, col);
    }
  }
}
const FAR_WINS = [];
FAR.forEach((fh, i) => {
  const w = fh.b - fh.a, spots = fh.cafe ? [] : [[0.9, 2.1, 0.95, 2.4], [w - 2.1, w - 0.9, 0.95, 2.4]];
  if (fh.h > 7) spots.push([0.9, 2.1, 3.6, 5], [w / 2 - 0.6, w / 2 + 0.6, 3.6, 5], [w - 2.1, w - 0.9, 3.6, 5]);
  spots.forEach(([d0, d1, h0, h1], k) => {
    const sc = F / FAR_Z, x = Math.round(VPX + (fh.a + d0) * sc), x2 = Math.round(VPX + (fh.a + d1) * sc);
    const y = Math.round(HZ - (FAR_B + h1) * sc), y2 = Math.round(HZ - (FAR_B + h0) * sc);
    FAR_WINS.push({ id: i * 10 + k, x, y, w: x2 - x, h: y2 - y, n: 2 + ((i * 7 + k * 3) % 5), off: hash(i, k, 190) });
  });
});

/* ================= The lane: cobbled setts, kerbs and pavements; the cross street at the bottom ================= */
const COB = ['#0b0b13', '#12111b', '#191722', '#211e2a', '#2a2632', '#35303a', '#433a40', '#544846', '#68584e', '#7e6a56'];
const PAVE = ['#101018', '#181820', '#222028', '#2c2a30', '#38343a', '#46403e', '#564e48', '#6a5e52'];
const ASPH = ['#0c0c14', '#121219', '#19181f', '#222026', '#2c282c', '#383032', '#463a36'];
const Y_BROW = HZ + F * SLOPE + F * EYE / CROSS.z0;  // where the lane meets the cross street (~216.4)
function renderGround() {
  const p = pix(W, H);
  for (let y = 205; y < H; y++) {
    const yc = y + 0.5, slope = yc > Y_BROW;
    const z = slope ? F * EYE / (yc - HZ - F * SLOPE) : -F * FAR_B / (yc - HZ);
    if (!slope && z > FAR_Z) continue;
    for (let x = 0; x < W; x++) {
      const lat = (x + 0.5 - VPX) * z / F, a = Math.abs(lat), alt = ground(z), L = lightAt(lat, z, alt);
      let col;
      if (!slope) {                                                          // the cross street
        if (z > CROSS.z1 - 0.9) col = pick(PAVE, 0.25 + L + (hash(x >> 2, y, 201) - 0.5) * 0.1, x, y);
        else if (z > CROSS.z1 - 1.05) col = '#4a4644';
        else col = pick(ASPH, 0.22 + L * 1.1 + (hash(x, y, 202) - 0.5) * 0.16 + (Math.abs(z - 54) < 0.12 && (Math.floor(lat / 1.5) & 1) ? 0.3 : 0), x, y);
      } else if (a <= KERB) {                                                // setts
        const row = Math.floor(z / 0.26), cq = lat / 0.3 + (row & 1) * 0.5, cl = Math.floor(cq), fu = cq - cl, fv = z / 0.26 - row;
        const zc = (row + 0.5) * 0.26, Lc = 0.24 + lightAt((cl + 0.5 - (row & 1) * 0.5) * 0.3, zc, ground(zc)) + (hash(cl, row, 203) - 0.5) * 0.16;
        const pxw = F * 0.3 / z, pxh = F * EYE * 0.26 / (z * z);
        let k = Math.round(Lc * (COB.length - 1));
        if (pxw > 2.2 && fu < 0.5 / pxw + 0.06) k = Math.max(0, k - 3);    // mortar between the stones
        else if (pxh > 1.4 && fv > 1 - 0.7 / pxh) k = Math.max(0, k - 3);
        else if (pxw > 3 && fu < 0.45 && fv < 0.5) k += 1;                   // the domed tops catch the light
        if (a > KERB - 0.35) k = clamp(Math.round((0.3 + L) * 6), 0, 9);     // the gutter's smooth channel
        col = COB[clamp(k, 0, COB.length - 1)];
      } else if (a < KERB + 0.16) col = pick(['#3a3838', '#5a5652', '#827a70', '#a49a8c'], 0.35 + L * 1.2, x, y);   // kerbstones
      else {                                                                 // pavement slabs
        const s = Math.floor(z / 0.6), sl = Math.floor(lat / 0.6), jz = z / 0.6 - s, jl = lat / 0.6 - sl;
        const joint = (F * 0.6 / z > 3 && jl < 0.07) || (F * EYE * 0.6 / (z * z) > 1.6 && jz < 0.08);
        col = pick(PAVE, 0.24 + L * 1.1 + (hash(sl, s, 204) - 0.5) * 0.12 - (joint ? 0.18 : 0), x, y);
      }
      p.set(x, y, col);
    }
  }
  const r = rng(205);                                                         // petals blown into the gutters and against the kerbs
  for (let k = 0; k < 110; k++) {                                             // spread evenly down the screen, not in depth
    const yy = Y_BROW + 1 + r() * (H - Y_BROW - 1), z = F * EYE / (yy - HZ - F * SLOPE), side = r() < 0.7 ? 1 : -1, lat = side * (KERB - 0.1 - r() * r() * 2.6);
    const [x, y] = proj(lat, z, ground(z)), c = PETAL_PAL[r() < 0.7 ? 0 : r() < 0.5 ? 1 : 2][r() < 0.5 ? 1 : 0];
    if (y >= H || y < Y_BROW) continue;
    p.set(x, y, c); if (z < 16 && r() < 0.6) p.set(x + 1, y, c);
  }
  return trim(p);
}

/* ================= A minibus taxi crossing at the bottom of the lane ================= */
const TAXI_ROWS = [
  '....wwwwwwwwwwwwwwwwww...', '...wgggwggggwggggwgggGw..', '..wwgggwggggwggggwgggGGw.', '.wwwwwwwwwwwwwwwwwwwwwwww',
  'wwwwwwwwwwwwwwwwwwwwwwwwL', 'wwssssssssssssssssssssssw', 'wwwwwwwwwwwwwwwwwwwwwwwww', 'dddddddddddddddddddddddd.', '...kkk.............kkk...'];
const TAXIS = [{ t0: 19, dir: 1 }, { t0: 72.5, dir: -1 }, { t0: 126, dir: 1 }, { t0: 187.5, dir: -1 }];
const TAXI_DUR = 3.8;
function renderTaxi() {
  const right = sprite(TAXI_ROWS, { w: '#dedee4', g: '#1a2030', G: '#2a3448', s: '#3a8a6a', d: '#3a3a44', k: '#0a0a0e', L: '#fff4d0' });
  const [left, lx] = makeCanvas(right.width, right.height);
  lx.translate(right.width, 0); lx.scale(-1, 1); lx.drawImage(right, 0, 0);
  return { right, left, w: right.width, h: right.height };
}
function drawTaxis(t) {
  for (const tx of TAXIS) {
    const s = (t - tx.t0) / TAXI_DUR;
    if (s < 0 || s >= 1) continue;
    const z = tx.dir > 0 ? 56 : 52, lat = tx.dir > 0 ? lerp(-17, 17, s) : lerp(17, -17, s);
    const [x, y] = proj(lat, z, FAR_B), X = Math.round(x) - (TAXI_C.w >> 1), Y = Math.round(y) - TAXI_C.h;
    ctx.drawImage(tx.dir > 0 ? TAXI_C.right : TAXI_C.left, X, Y);
    const fx = tx.dir > 0 ? X + TAXI_C.w : X - 1, bx = tx.dir > 0 ? X - 1 : X + TAXI_C.w;
    halo(HL.head, fx, Y + 4, 0.9); halo(HL.beam, fx + tx.dir * 10, Y + 7, 0.7); halo(HL.tail, bx, Y + 5, 0.8);
    rect(ctx, bx, Y + 4, 1, 2, '#ff3a2a');
  }
}

/* ================= The lamps: two wall lanterns and a lamp post at the bottom ================= */
const LAMP_FLICK = [[92.4, 93.8], [201.2, 202]];
function drawLantern(l, lv) {
  const x = Math.round(l.x), y = Math.round(l.y);
  rect(ctx, x - 1, y - 5, 3, 1, '#141218'); rect(ctx, x - 2, y - 4, 5, 1, '#1c1a20');
  rect(ctx, x - 2, y - 3, 5, 5, lv > 0.5 ? '#ffe2a4' : '#7a5838'); if (lv > 0.5) rect(ctx, x - 1, y - 2, 3, 3, '#fff6dc');
  rect(ctx, x, y - 3, 1, 5, '#3a2a24'); rect(ctx, x - 2, y + 2, 5, 1, '#1c1a20');
  halo(HL.lamp, x, y, lv); halo(HL.lampCore, x, y - 1, lv);
}
function drawLamps(t) {
  drawLantern(LAMPS[0], 0.95 + 0.05 * wave(t, 1500, 0.1));
  drawLantern(LAMPS[1], 0.95 + 0.05 * wave(t, 1300, 0.4));
  const lp = LAMPS[2], lv = (0.95 + 0.05 * wave(t, 1700, 0.3)) * flick(t, LAMP_FLICK, 9), x = Math.round(lp.x), y = Math.round(lp.y);
  rect(ctx, x - 2, y - 1, 5, 1, '#1a1820'); rect(ctx, x - 1, y, 3, 2, lv > 0.5 ? '#fff0c8' : '#6a5a48');
  halo(HL.post, x, y + 1, lv); halo(HL.lampCore, x, y + 1, lv * 0.8);
  return lv;
}
function drawPools(t, lvPost) {
  drawPool(POOL.l, ...proj(LFAC + 1.6, 10.3, ground(10.3)), 0.9);
  drawPool(POOL.r, ...proj(RFAC - 1.8, 11.6, ground(11.6)), 0.9);
  drawPool(POOL.post, LAMPS[2].x + 2, proj(-3.9, 38, ground(38))[1] - 1, lvPost);
  drawPool(POOL.cafe, 259, 209, 0.8 + 0.1 * wave(t, 900));
}

/* ================= Windows switching on and off (lit overlays over the dark glass) ================= */
function drawWindows(t, list) {
  for (const w of list) {
    if (w.p < 2 && hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 4) >= w.p) continue;
    ctx.drawImage(w.c, w.x, w.y);
    halo(w.big ? HL.win : HL.winS, w.cx, w.cy, w.p >= 2 ? 1 : 0.8);
  }
}
function drawFarWindows(t) {
  for (const w of FAR_WINS) {
    if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 191) >= 0.6) continue;
    rect(ctx, w.x, w.y, w.w, w.h, '#f4a852'); rect(ctx, w.x, w.y, w.w, 1, '#ffd08a');
  }
  const cf = flick(t, CAFE_FLICK, 12);
  text(ctx, 'CAFE', 252, 193, cf > 0.5 ? '#fff0e0' : '#6a3a3a');
  halo(HL.cafe, 259, 195, cf);
  halo(HL.shop, 259, 203, 0.8);
}
const CAFE_FLICK = [[44.2, 45.4], [161, 162.2]];

/* ================= Neighbours on the turquoise house's stoep: an uncle in a kufi and an auntie with her tea ================= */
// pre-rendered poses; o = the seat point. They sit on the bench against the wall, facing the lane (left on screen).
function seg(p, x0, y0, x1, y1, w, col) {
  const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 2));
  for (let k = 0; k <= n; k++) { const x = lerp(x0, x1, k / n), y = lerp(y0, y1, k / n); p.fill(Math.round(x - w / 2), Math.round(y - w / 2), Math.max(1, Math.round(w)), Math.max(1, Math.round(w)), col); }
}
const SIT_O = { x: 18, y: 30 };
function renderSitter(kind, pose, u) {
  const p = pix(34, 44), X = v => SIT_O.x + v * u, Y = v => SIT_O.y + v * u;
  const man = kind === 'man', look = pose === 'look';
  const skin = man ? '#6a4028' : '#7a4a30', cloth = man ? '#4a3626' : '#2a5a78', dark = man ? '#2e2218' : '#1a3a52';
  // legs: thighs forward to the knee, shins down to the stoep floor, shoes
  if (man) { seg(p, X(0), Y(-0.6), X(-4.2), Y(-0.9), 1.4 * u, '#8a8474'); seg(p, X(-4.2), Y(-0.6), X(-4.4), Y(4.1), 1.1 * u, '#76705f'); p.fill(Math.round(X(-5.6)), Math.round(Y(4.1)), Math.round(1.8 * u), Math.max(1, Math.round(0.6 * u)), '#141012'); }
  else { seg(p, X(0), Y(-0.6), X(-4), Y(-0.8), 1.8 * u, cloth); for (let j = 0; j < 4.4 * u; j++) p.fill(Math.round(X(-4.9)), Math.round(Y(-0.4) + j), Math.round(1.6 * u + j * 0.1), 1, j % 3 === 0 ? dark : cloth); }
  // torso, leaning back a little against the wall
  for (let j = 0; j < 5.6 * u; j++) p.fill(Math.round(X(-1.2) + j * 0.06), Math.round(Y(-0.4) - j), Math.round(2.4 * u), 1, j > 4.8 * u && man ? '#d8d4c8' : cloth);
  p.fill(Math.round(X(0.9)), Math.round(Y(-5.6)), 1, Math.round(5 * u), dark);                                             // shadowed back
  // head
  const hx = X(-0.9) + (look ? -0.6 * u : 0), hy = Y(-8.3);
  p.fill(Math.round(hx - 0.2 * u), Math.round(hy + 0.5 * u), Math.round(2.1 * u), Math.round(2.5 * u), skin);
  p.set(Math.round(hx - 0.2 * u) - 1, Math.round(hy + 1.3 * u), skin);                                                   // nose
  p.set(Math.round(hx + 0.2 * u), Math.round(hy + 1.1 * u), '#1a1010');                                                 // eye
  if (man) {
    p.fill(Math.round(hx - 0.2 * u), Math.round(hy), Math.round(2.1 * u), Math.round(0.7 * u), '#ece8dc');               // kufi
    p.fill(Math.round(hx - 0.2 * u), Math.round(hy + 2.2 * u), Math.round(1.9 * u), Math.round(0.9 * u), '#b0a89c');     // grey beard
  } else {
    p.fill(Math.round(hx - 0.3 * u), Math.round(hy - 0.3 * u), Math.round(2.6 * u), Math.round(0.9 * u), '#d84a7a');     // headscarf
    p.fill(Math.round(hx + 1.2 * u), Math.round(hy), Math.round(1.2 * u), Math.round(3.6 * u), '#b83a66');
    p.fill(Math.round(hx - 0.3 * u), Math.round(hy - 0.3 * u), Math.round(2 * u), 1, '#f27aa2');
  }
  // arms
  const sx = X(-0.8), sy = Y(-5);
  if (pose === 'sip') { seg(p, sx, sy, X(-2), Y(-3), 0.9 * u, cloth); seg(p, X(-2), Y(-3), X(-2.2), Y(-6.4), 0.8 * u, skin); p.fill(Math.round(X(-2.8)), Math.round(Y(-7)), Math.round(1.1 * u), Math.round(1 * u), '#f4f0e8'); }
  else if (pose === 'talk') { seg(p, sx, sy, X(-2.4), Y(-2.6), 0.9 * u, cloth); seg(p, X(-2.4), Y(-2.6), X(-4.2), Y(-4.6), 0.8 * u, skin); }
  else if (pose === 'talk2') { seg(p, sx, sy, X(-2.4), Y(-3), 0.9 * u, cloth); seg(p, X(-2.4), Y(-3), X(-4.4), Y(-2.2), 0.8 * u, skin); }
  else { seg(p, sx, sy, X(-2), Y(-2.2), 0.9 * u, cloth); seg(p, X(-2), Y(-2.2), X(-3.6), Y(-1.6), 0.8 * u, skin); if (!man) p.fill(Math.round(X(-4.4)), Math.round(Y(-2.4)), Math.round(1.1 * u), Math.round(1 * u), '#f4f0e8'); }
  return p.done();
}
const SEATS = [{ kind: 'man', z: 10.5 }, { kind: 'woman', z: 11.5 }].map(s => {
  const [x, y] = proj(RFAC - 0.3, s.z, HOUSES[7].b + 0.48);
  return { ...s, x: Math.round(x), y: Math.round(y), u: F / s.z / 10 };
});
const HORNS = [47.2, 131.6, 206.4];                   // a ship's horn from the harbour, behind us to the left
const heardHorn = t => HORNS.some(h => t >= h + 0.3 && t < h + 4.2);
function drawSitters(t) {
  const look = heardHorn(t);
  const talk = step(t, 480), manPose = look ? 'look' : wave(t, 24, 0.1) > 0.2 ? (hash(talk, 5, 211) < 0.5 ? 'talk' : 'talk2') : 'rest';
  const sip = frac(t / 26) > 0.82 && !look ? 'sip' : look ? 'look' : 'rest';
  const W1 = SIT_C.woman[sip], M1 = SIT_C.man[manPose];
  ctx.drawImage(W1, SEATS[1].x - SIT_O.x, SEATS[1].y - SIT_O.y);
  ctx.drawImage(M1, SEATS[0].x - SIT_O.x, SEATS[0].y - SIT_O.y);
  if (sip === 'sip' && frac(t / 26) < 0.9) { const s = frac(t * 0.7); ctx.globalAlpha = 0.4 * (1 - s); rect(ctx, SEATS[1].x - 7, SEATS[1].y - 18 - Math.round(s * 5), 1, 1, '#f0f0f8'); ctx.globalAlpha = 1; }
}

/* ================= A cat on the lime house's parapet, hadedas roosting on the yellow one's ================= */
const CAT = (() => { const [x, y] = proj(LFAC, 17.4, HOUSES[1].b + HOUSES[1].h); return { x: Math.round(x), y: Math.round(y) }; })();
function drawCat(t) {
  const { x, y } = CAT, g = gust(t);
  rect(ctx, x - 3, y - 4, 6, 4, '#0c0a10'); rect(ctx, x + 2, y - 7, 4, 4, '#0c0a10'); rect(ctx, x + 2, y - 8, 1, 1, '#0c0a10'); rect(ctx, x + 5, y - 8, 1, 1, '#0c0a10');
  if (g > 0.6 && frac(t / 3) < 0.5) rect(ctx, x + 5, y - 8, 1, 1, '#1a1620');            // an ear flattened in the gust
  if (!(frac(t / 6) > 0.95 || frac(t / 41) > 0.985)) { rect(ctx, x + 3, y - 6, 1, 1, '#d8f060'); rect(ctx, x + 5, y - 6, 1, 1, '#d8f060'); }
  const sw = wave(t, 28, 0.2) * 1.3 + wave(t, 83, 0.6) * 0.5 + g * 1.5;
  for (let j = 0; j < 8; j++) rect(ctx, x - 3 - Math.round(Math.max(0, sw) * j / 7), y + j, 1, 1, '#0c0a10');   // the tail hangs down the wall, blown about
}
const HADEDAS = [17.2, 18.9].map((z, i) => { const [x, y] = proj(RFAC, z, HOUSES[8].b + HOUSES[8].h); return { x: Math.round(x), y: Math.round(y), i }; });
function drawHadedas(t) {
  for (const hd of HADEDAS) {                                                 // hunched on the parapet, facing the lane
    const { x, y, i } = hd, q = frac(t / (i ? 31 : 23) + i * 0.4), up = q > 0.66, preen = q > 0.84 && q < 0.92;
    rect(ctx, x - 3, y - 4, 7, 3, '#3a362e'); rect(ctx, x - 2, y - 5, 5, 1, '#4e4a40'); rect(ctx, x - 3, y - 4, 1, 1, '#4e4a40');
    rect(ctx, x + 3, y - 3, 2, 1, '#2a2822'); rect(ctx, x + 1, y - 4, 2, 1, '#3e7a5a');      // tail, the green-bronze shoulder
    rect(ctx, x - 1, y - 1, 1, 1, '#8a4a3a'); rect(ctx, x + 1, y - 1, 1, 1, '#8a4a3a');      // legs
    if (up) {                                                                 // head up, the long decurved bill
      rect(ctx, x - 4, y - 7, 2, 3, '#443f36'); rect(ctx, x - 5, y - 8, 2, 2, '#4e4a40'); rect(ctx, x - 5, y - 8, 1, 1, '#b8b0a0');
      if (preen) { rect(ctx, x - 4, y - 5, 1, 1, '#1c1a18'); rect(ctx, x - 3, y - 4, 1, 1, '#1c1a18'); rect(ctx, x - 2, y - 4, 1, 1, '#1c1a18'); }
      else { rect(ctx, x - 6, y - 8, 1, 1, '#1c1a18'); rect(ctx, x - 7, y - 7, 1, 1, '#1c1a18'); rect(ctx, x - 8, y - 6, 1, 1, '#1c1a18'); rect(ctx, x - 8, y - 5, 1, 1, '#1c1a18'); }
    } else { rect(ctx, x - 5, y - 6, 3, 2, '#443f36'); rect(ctx, x - 6, y - 5, 1, 1, '#1c1a18'); rect(ctx, x - 7, y - 4, 1, 1, '#1c1a18'); }   // head sunk into the shoulders
  }
}

/* ================= Washing on the lime house's roof, flying in the south-easter ================= */
const LINE0 = proj(LFAC - 0.9, 14.8, HOUSES[1].b + HOUSES[1].h + 1.5), LINE1 = proj(LFAC - 0.9, 18.8, HOUSES[1].b + HOUSES[1].h + 1.5);
const lineY = u => lerp(LINE0[1], LINE1[1], u) + 2 * Math.sin(Math.PI * u);
const WASH = [{ u: 0.06, w: 6, len: 8, col: '#dcd8e4' }, { u: 0.32, w: 5, len: 9, col: '#b83a44' }, { u: 0.57, w: 4, len: 6, col: '#3a5a92' },
  { u: 0.78, w: 5, len: 7, col: '#d8b840' }];
const LINE_SLOPE = (LINE1[1] - LINE0[1]) / (LINE1[0] - LINE0[0]);
function drawWashing(t) {
  ctx.fillStyle = '#16141c';
  for (const [x, y] of [LINE0, LINE1]) ctx.fillRect(Math.round(x), Math.round(y), 1, 7);
  for (let k = 0; k <= 30; k++) { const u = k / 30; ctx.fillRect(Math.round(lerp(LINE0[0], LINE1[0], u)), Math.round(lineY(u)), 1, 1); }
  const g = gust(t);
  WASH.forEach((it, i) => {                                                   // pegged along the line, blown out to the right
    const x0 = lerp(LINE0[0], LINE1[0], it.u), y0 = lineY(it.u) + 1, shade = CLOTH_SHADE[it.col];
    const lean = 0.32 + 0.4 * g + 0.12 * wave(t, 53 + i * 7, i * 0.2);
    for (let j = 0; j < it.len; j++) {
      const f = j / it.len, rip = Math.sin(TAU * (460 + i * 37) * t / LOOP - j * 1.1 + i) * f * (0.6 + 0.9 * g);
      const x = x0 + j * lean + rip, y = y0 + j * (1 - 0.25 * g);
      for (let k = 0; k < it.w; k++) rect(ctx, Math.round(x + k), Math.round(y + k * LINE_SLOPE), 1, 1, k === it.w - 1 || (j === it.len - 1) ? shade : it.col);
    }
    rect(ctx, Math.round(x0), Math.round(y0 - 1), 1, 1, '#8a7a6a'); rect(ctx, Math.round(x0 + it.w - 1), Math.round(y0 - 1 + (it.w - 1) * LINE_SLOPE), 1, 1, '#8a7a6a');   // pegs
  });
}
const CLOTH_SHADE = {};

/* ================= Walkers: a couple going down to the mosque, a man coming up with the shopping ================= */
// the couple steps out of the blue house's door and heads down; the man comes up and goes in at the lime house's door
const COUPLE = { t0: 30, t1: 74, z0: 22.4, z1: 47, lat0: -5.6, lat: -1.3, turn: [0, 0.16] };
const UPHILL = { t0: 146, t1: 190, z0: 46, z1: 16.2, lat0: -5.7, lat: 1.7, turn: [0.72, 1] };
function makeCouple(h, fr) {
  const wM = Math.max(3, Math.round(h * 0.25)), wW = Math.max(3, Math.round(h * 0.25)), gap = Math.max(1, Math.round(h * 0.06));
  const p = pix(wM + gap + wW, h + 1);
  const body = (x0, pw, ph, top, low, head, dress, st) => {
    const y0 = h - ph, hh = Math.max(2, Math.round(ph * 0.14)), torso = Math.max(2, Math.round(ph * (dress ? 0.3 : 0.44))), legs = ph - hh - torso;
    const hw = Math.max(2, Math.round(pw * 0.6)), hx = x0 + ((pw - hw) >> 1);
    p.fill(hx, y0, hw, hh, head); if (dress) p.fill(hx - (hw > 2 ? 1 : 0), y0 + hh, hw + (hw > 2 ? 2 : 0), Math.max(1, Math.round(hh * 0.9)), head);
    else p.fill(hx, y0 + hh - 1, hw, 1, '#2a1a12');
    p.fill(x0, y0 + hh, pw, torso, top); p.fill(x0 + pw - 1, y0 + hh, 1, torso, '#8a8478');
    const ly = y0 + hh + torso, lw = Math.max(1, (pw - 1) >> 1);
    if (dress) { p.fill(x0, ly, pw, legs - 1, low); p.fill(x0 + (st ? 0 : pw - 1), h - 1, 1, 1, '#141014'); }
    else { p.fill(x0 + (st ? 0 : 1), ly, lw, h - ly, low); p.fill(x0 + pw - lw - (st ? 1 : 0), ly, lw, h - ly - (st ? 0 : 1), low); }
  };
  body(0, wM, h, '#e8e4dc', '#2a2a36', '#f2eee4', false, fr);                   // white kufi and kurta
  body(wM + gap, wW, Math.round(h * 0.93), '#2a5a7a', '#24506e', '#8a3a8a', true, !fr);   // headscarf, long dress
  p.fill(wM - 1, Math.round(h * 0.5), gap + 2, 1, '#8a5a3a');                        // holding hands
  return p.done();
}
function makeWalker(h, fr) {                                                         // facing us: fez, white shirt, a bag
  const w = Math.max(3, Math.round(h * 0.27)), p = pix(w + 3, h + 1);
  const hh = Math.max(2, Math.round(h * 0.14)), torso = Math.round(h * 0.4), hw = Math.max(2, Math.round(w * 0.6)), hx = (w - hw) >> 1;
  p.fill(hx, 0, hw, Math.max(1, Math.round(hh * 0.45)), '#b8242a'); p.fill(hx, Math.round(hh * 0.45), hw, hh - Math.round(hh * 0.45), '#7a4a30');
  if (h > 20) p.set(hx + 1, Math.round(hh * 0.7), '#1a1010');
  p.fill(0, hh, w, torso, '#e4e0d4'); p.fill(0, hh, 1, torso, '#9a968c');
  const ly = hh + torso, lw = Math.max(1, (w - 1) >> 1);
  p.fill(fr ? 0 : 1, ly, lw, h - ly, '#34343e'); p.fill(w - lw - (fr ? 1 : 0), ly, lw, h - ly - (fr ? 0 : 1), '#34343e');
  const bh = Math.max(2, Math.round(h * 0.18));
  p.fill(w, hh + torso - 2, Math.max(1, Math.round(w * 0.5)), bh, '#d8c49a'); p.fill(w, hh + torso - 2, 1, 1, '#f0e0b8');
  return p.done();
}
function drawWalkers(t) {
  for (const [q, spr] of [[COUPLE, COUPLE_C], [UPHILL, UPHILL_C]]) {
    if (t < q.t0 || t >= q.t1) continue;
    const s = (t - q.t0) / (q.t1 - q.t0), z = lerp(q.z0, q.z1, s), h = clamp(Math.round(F * 1.7 / z), 8, 42);
    const [ta, tb] = q.turn, k = ta === 0 ? 1 - smooth(ta, tb, s) : smooth(ta, tb, s), lat = lerp(q.lat, q.lat0, k);
    const im = spr[h][Math.floor(t * (1.8 + 18 / z)) & 1], [x, y] = proj(lat, z, ground(z));
    ctx.globalAlpha = Math.min(1, s / 0.05, (1 - s) / 0.05);
    ctx.drawImage(im, Math.round(x - im.width / 2), Math.round(y - h));
    ctx.globalAlpha = 1;
  }
}

/* ================= Protea petals and fynbos bells, blown almost sideways by the south-easter at three depths ================= */
// Each petal wraps round the frame k (integer) times per loop, pushed further along in the gusts (gustPush is periodic),
// and takes a new height each time round (hash of the wrap index modulo k), so t and t + 240 match.
const SPAN = W + 40;
const PETAL_DEPTHS = [
  { count: 46, kMin: 14, kMax: 22, y: [56, 190], fall: 16, bob: 3, push: 22 },
  { count: 30, kMin: 28, kMax: 40, y: [74, 228], fall: 26, bob: 5, push: 40 },
  { count: 16, kMin: 50, kMax: 66, y: [24, 256], fall: 40, bob: 9, push: 72 },
];
const PETALS = PETAL_DEPTHS.map((D, depth) => {
  const r = rng(990 + depth), out = [];
  for (let i = 0; i < D.count; i++) out.push({
    id: depth * 100 + i, k: D.kMin + Math.floor(r() * (D.kMax - D.kMin + 1)), x0: r() * SPAN,
    kb: 20 + Math.floor(r() * 30), pb: r(), ks: 30 + Math.floor(r() * 60), ps: r(), kf: 40 + Math.floor(r() * 50), pf: r(),
    kind: r() < 0.62 ? 0 : r() < 0.6 ? 1 : 2,
  });
  return out;
});
const PETAL_PAL = [['#a8305e', '#e86a98', '#ffb0cc'], ['#c07a78', '#f2c2b4', '#fff0e4'], ['#80145a', '#c8288a', '#f05ab0']];
function makePetal(size, pal, back) {
  const w = size === 1 ? 4 : 7, h = size === 1 ? 2 : 3, p = pix(w, h);
  if (size === 1) { p.set(0, 1, pal[0]); p.set(1, 1, pal[1]); p.set(1, 0, pal[1]); p.set(2, 0, back ? pal[1] : pal[2]); p.set(3, 0, pal[1]); return p.done(); }
  const rows = ['..0112.', '0111122', '.0011..'];
  rows.forEach((row, j) => { for (let i = 0; i < w; i++) if (row[i] !== '.') p.set(i, j, pal[back ? Math.min(1, +row[i]) : +row[i]]); });
  return p.done();
}
function makeBell(pal) { const p = pix(3, 3); p.set(1, 0, pal[1]); p.set(0, 1, pal[1]); p.set(1, 1, pal[2]); p.set(2, 1, pal[1]); p.set(1, 2, pal[0]); return p.done(); }
function drawPetals(depth, t) {
  const D = PETAL_DEPTHS[depth], push = gustPush(t) * D.push;
  for (const pt of PETALS[depth]) {
    const X = pt.x0 + pt.k * SPAN * t / LOOP + push, w = Math.floor(X / SPAN), x = X - w * SPAN - 20, wi = ((w % pt.k) + pt.k) % pt.k;
    const y = lerp(D.y[0], D.y[1], hash(pt.id, wi, 1)) + D.fall * (x + 20) / SPAN + D.bob * wave(t, pt.kb, pt.pb);
    if (x < -8 || x > W + 8 || y < -8 || y > H + 8) continue;
    const fl = Math.cos(TAU * (pt.kf * t / LOOP + pt.pf)), pal = PETAL_PAL[pt.kind];
    if (depth === 0) {
      ctx.fillStyle = pal[1]; ctx.globalAlpha = 0.85;
      ctx.fillRect(Math.round(x), Math.round(y), Math.abs(fl) > 0.55 ? 2 : 1, 1);
      continue;
    }
    if (pt.kind === 2) { ctx.drawImage(BELL_C, Math.round(x) - 1, Math.round(y) - 1); continue; }
    const spr = PETAL_SPR[depth][pt.kind][fl < 0 ? 1 : 0];
    ctx.save();
    ctx.translate(Math.round(x), Math.round(y)); ctx.rotate(0.25 + 0.9 * wave(t, pt.ks, pt.ps)); ctx.scale(1, Math.max(0.3, Math.abs(fl)));
    ctx.drawImage(spr, -(spr.width >> 1), -(spr.height >> 1));
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

/* ================= A picked weather (site/js/engine/weather.js) ================= */
// Cape Town draws its own protea petals (only with its own weather); rain, snow and leaves land over the city bowl, on the
// cross street and past the bottom of the frame, and catch the lanterns, the lamp post, the café and the minaret's lights
const WEATHER = weatherPlans(['protea']);
const WX_LAND = [[128, 186], [198, 216], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: LAMPS[0].x, y: LAMPS[0].y + 18, rx: 30, ry: 44 }, { x: LAMPS[1].x, y: LAMPS[1].y + 18, rx: 30, ry: 44 },
  { x: LAMPS[2].x, y: LAMPS[2].y + 14, rx: 16, ry: 26 }, { x: 259, y: 200, rx: 14, ry: 10, col: '#ffd8a0' },
  { x: MX, y: 128, rx: 8, ry: 10, col: '#9affc0' }, { x: 236, y: 64, rx: 90, ry: 14, col: '#e8e4f4' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + 23, y: BB.y + 10, rx: 30, ry: 18, col: '#ffe8c8' }] };

/* ================= Pre-render everything static ================= */
let HL, SKY_C, CLOUD_C, MOUNT_C, CITY_C, PALM_C, MIN_C, WALL_BB, WALL_BARE, AD, BB_PITCH, FAR_C, GROUND_C, FG_C, TAXI_C, POOL,
  SIT_C, COUPLE_C, UPHILL_C, PETAL_SPR, BELL_C, VIG_C;
function renderForeground() {
  const p = pix(W, H);
  WINS = [];
  const order = [...HOUSES].sort((a, b) => b.za - a.za);                     // far to near
  for (const hs of order) paintFacade(p, hs);
  for (const hs of order) if (hs.stoep) paintStoep(p, hs);
  for (const [hi, geranium] of [[0, 1], [1, 0], [8, 1], [9, 0]]) {            // potted geraniums and a spekboom on the stoeps' corners
    const hs = HOUSES[hi], s = hs.side, z = hs.za + hs.w / 2 - 1.3 + 0.35, [x, y] = proj(hs.lat - s * 1.05, z, hs.b + 0.03).map(Math.round), k = F / z / 10;
    const pw = Math.max(2, Math.round(k * 2.6)), ph = Math.max(2, Math.round(k * 2.8));
    p.fill(x - (pw >> 1), y - ph, pw, ph, '#9a4a2a'); p.fill(x - (pw >> 1), y - ph, pw, 1, '#c86a3a');
    for (let j = 0; j < ph * 1.6; j++) for (let i = -pw; i <= pw; i++) {
      const u = i / (pw + 0.5), v = j / (ph * 1.6);
      if (u * u + (v - 0.5) ** 2 * 3 > 0.9 || hash(x + i, j, 206) < 0.15) continue;
      p.set(x + i, y - ph - 1 - j, geranium && hash(x + i, j, 207) < 0.28 ? (hash(i, j, 208) < 0.5 ? '#e83a4a' : '#ff6a6a') : (i < 0 ? '#2e5a2e' : '#1e3e22'));
    }
  }
  const lp = LAMPS[2], [, yb] = proj(lp.lat, lp.z, ground(lp.z));             // the lamp post
  for (let y = Math.round(lp.y) + 1; y < Math.round(yb); y++) { p.set(Math.round(lp.x), y, '#15131a'); if (y > yb - 4) p.set(Math.round(lp.x) - 1, y, '#15131a'); }
  p.set(Math.round(lp.x) + 1, Math.round(lp.y) + 1, '#15131a');
  for (const l of LAMPS.slice(0, 2)) { const s = l.lat < 0 ? -1 : 1; for (let k = 0; k < 4; k++) p.set(Math.round(l.x) + s * k, Math.round(l.y) - 6, '#15131a'); }   // lantern brackets
  return p.done();
}
function renderVignette() {
  const p = pix(W, H), c = [8, 4, 18];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function prerender() {
  HL = {
    crux: makeHalo(3, '#dfe8ff', 0.35, 1), cruxG: makeHalo(3, '#ffc890', 0.3, 1), strobe: makeHalo(4, '#e8f0ff', 0.6, 1),
    station: makeHalo(8, '#ffd08a', 0.4), stationUp: makeHalo(7, '#ffe6c0', 0.3), cabin: makeHalo(5, '#ffd890', 0.45),
    headlamp: makeHalo(3, '#f0f4ff', 0.4, 1), carFar: makeHalo(4, '#fff0d0', 0.4, 1), red: makeHalo(3, '#ff3a3a', 0.5, 1),
    green: makeHalo(9, '#5aff8a', 0.28), greenTop: makeHalo(5, '#8affaa', 0.25), bbGlow: makeHalo(32, '#ffe0c0', 0.1),
    bbLamp: makeHalo(6, '#ffe8c8', 0.5), head: makeHalo(6, '#fff2d0', 0.5), beam: makeHalo(12, '#fff0c8', 0.16, 1.2),
    tail: makeHalo(3, '#ff3a2a', 0.5, 1), lamp: makeHalo(40, '#ffd49a', 0.16, 1.7), lampCore: makeHalo(10, '#fff4dc', 0.42, 1.4),
    post: makeHalo(30, '#ffd49a', 0.18, 1.6), win: makeHalo(14, '#ffb45a', 0.15), winS: makeHalo(7, '#ffb45a', 0.14),
    cafe: makeHalo(10, '#ff9a8a', 0.3), shop: makeHalo(14, '#ffc070', 0.22),
  };
  SKY_C = renderSky(); STARS = makeStars(); CLOUD_C = renderClouds(); MOUNT_C = renderMountains(); buildCloth();
  CITY_C = renderCity(); PALM_C = PALMS.map(pm => Array.from({ length: PALM_LEANS }, (_, k) => renderPalmCrown(k / (PALM_LEANS - 1), pm.s)));
  MIN_C = renderMinaret(); WALL_BB = renderGableWall(true); WALL_BARE = renderGableWall(false);
  AD = renderAd(); BB_PITCH = makePitch(BB.w, BB.h, { accent: '#e8f55c' });
  { const p = pix(W, H); paintFar(p); FAR_C = trim(p); }
  GROUND_C = renderGround(); FG_C = { c: renderForeground(), x: 0, y: 0 }; TAXI_C = renderTaxi();
  POOL = { l: makePool(30, 6, '#ffc880', 0.26), r: makePool(28, 6, '#ffc880', 0.26), post: makePool(22, 4, '#ffc880', 0.26), cafe: makePool(14, 2, '#ffc070', 0.25) };
  SIT_C = {};
  for (const s of SEATS) { SIT_C[s.kind] = {}; for (const pose of ['rest', 'talk', 'talk2', 'sip', 'look']) SIT_C[s.kind][pose] = renderSitter(s.kind, pose, s.u); }
  COUPLE_C = []; UPHILL_C = [];
  for (let h = 8; h <= 42; h++) { COUPLE_C[h] = [makeCouple(h, 0), makeCouple(h, 1)]; UPHILL_C[h] = [makeWalker(h, 0), makeWalker(h, 1)]; }
  for (const it of WASH) CLOTH_SHADE[it.col] = mix(it.col, '#1a1030', 0.45);
  PETAL_SPR = [null, 1, 2].map(s => s && PETAL_PAL.map(pl => [makePetal(s, pl, false), makePetal(s, pl, true)]));
  BELL_C = makeBell(PETAL_PAL[2]);
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
/** render(t) paints the city's own koesister ad; { pitch } paints the site's face over it; { billboard: false } leaves out
 *  the ad, its border and lamps (the gable wall stays, bare); { weather } draws a WEATHERS pick */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  ctx.drawImage(SKY_C, 0, 0);
  drawStars(t);
  const off = Math.floor(frac(t / LOOP) * W);
  ctx.drawImage(CLOUD_C, off - W, 6); ctx.drawImage(CLOUD_C, off, 6);
  drawPlane(t);
  drawSatellite(t);
  blit(MOUNT_C);
  drawMountainFx(t);
  drawCloth(t);
  halo(HL.stationUp, CAB.x0 - 1, CAB.y0 - 3, 0.45 + 0.1 * wave(t, 9, 0.2));
  drawCity(t);
  drawPalms(t);
  drawGulls(t);
  if (wx.protea) drawPetals(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawMinaret(t);
  blit(billboard ? WALL_BB : WALL_BARE);
  if (billboard) drawBillboard(t, pitch);
  blit(FAR_C); drawFarWindows(t);
  drawTaxis(t);
  blit(GROUND_C);
  const lvPost = (0.95 + 0.05 * wave(t, 1700, 0.3)) * flick(t, LAMP_FLICK, 9);
  drawPools(t, lvPost);
  if (wx.protea) drawPetals(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  blit(FG_C);
  drawWindows(t, WINS);
  drawLamps(t);
  drawSitters(t);
  drawCat(t);
  drawHadedas(t);
  drawWashing(t);
  drawWalkers(t);
  if (wx.protea) drawPetals(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'cape-town',
  name: 'Cape Town',
  tagline: 'Table Mountain from Bo-Kaap',
  timeZone: 'Africa/Johannesburg',
  accent: '#e8f55c',
  focusX: 236,
  landmark: [TM.x0, TM.x1],                           // Table Mountain's flat top, eastern buttress to the western end
  landmarkTop: CAB.y0 - 8,                            // the upper cable station's roof (the tablecloth stays below it)
  billboard: BB,
  ambience: { wind: 0.5, crickets: 0.26, crowd: 0.1, traffic: 0.12 },
  events: [...HORNS.map(t => ({ t, type: 'horn', pan: -0.85, gain: 0.5 })),
    { t: 38, type: 'gull', x: 150 }, { t: 112.5, type: 'gull', x: 292 }, { t: 171, type: 'gull', x: 226 }].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
